import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import {
  ArrowDown,
  ArrowUp,
  CheckCircle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Clock,
  Download,
  Pencil,
  Plus,
  RotateCcw,
  Sparkles,
  Trash2,
} from 'lucide-react';
import {
  AnimalABCsSimplified,
  PepeBreathingSimplified,
  PekeStorySimplified,
  SuperpowersCardsSimplified,
} from './teaching/SimplifiedGameComponents';
import { useTeachingSession } from '../hooks/useTeachingSession';
import { AVATAR_FACTS } from '../data/avatarFacts';
import {
  PLAY_ACTIVITIES,
  PLAY_CATEGORY_LABELS,
  type PlayActivity,
  type PlayCategory,
} from '../data/playActivities';
import type {
  ReflectionNotes,
  StudentAvatarRecord,
} from '../lib/teachingSession';
import { SessionStatusChip } from './teaching/SessionStatusChip';
import { StartSessionScreen } from './teaching/StartSessionScreen';
import { PhotoCaptureGallery } from './teaching/PhotoCaptureGallery';
import { PhotoReviewList } from './teaching/PhotoReviewList';
import { SKILL_TAG_COLORS, SKILL_TAG_LABELS } from './teaching/skillTags';
import { GlobalNav } from './layout/GlobalNav';
import type { PhotoWithUrl } from '../lib/teachingSession';

// ── Download helpers (module scope) ─────────────────────────────────────────
async function fetchAsBlob(url: string): Promise<Blob> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
  return res.blob();
}

function triggerDownload(blob: Blob, filename: string) {
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke on next tick so the download has a chance to start.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

/**
 * Compose a single photo with its caption + student emojis + skill tags
 * baked into a footer strip below the image. If the photo has no metadata,
 * the original blob is returned untouched.
 */
async function composePhotoWithCaption(
  photo: PhotoWithUrl,
  avatars: { emoji: string; name: string }[],
  language: 'en' | 'es',
): Promise<Blob> {
  const photoBlob = await fetchAsBlob(photo.publicUrl);

  // Collect text to overlay
  const captionText = photo.caption?.trim() ?? '';
  const studentLine = photo.student_tags
    .map((idx) => avatars[idx]?.emoji ?? '')
    .filter(Boolean)
    .join(' ');
  const skillLine = photo.skill_tags
    .map((s) => SKILL_TAG_LABELS[s][language])
    .join(' · ');

  if (!captionText && !studentLine && !skillLine) return photoBlob;

  // Decode the photo into a bitmap (avoids the canvas tainting issue that
  // <img crossOrigin> can run into; createImageBitmap on a Blob is local).
  const bitmap = await createImageBitmap(photoBlob);

  const fontSize = Math.max(20, Math.round(bitmap.width / 32));
  const padding = Math.round(fontSize * 0.9);
  const lineHeight = Math.round(fontSize * 1.35);

  // Pre-measure to compute strip height — needs a temp ctx.
  const measure = document.createElement('canvas').getContext('2d')!;
  measure.font = `${fontSize}px -apple-system, "Segoe UI", Inter, sans-serif`;

  const wrap = (text: string, maxWidth: number): string[] => {
    if (!text) return [];
    const words = text.split(/\s+/);
    const lines: string[] = [];
    let line = '';
    for (const w of words) {
      const probe = line ? `${line} ${w}` : w;
      if (measure.measureText(probe).width > maxWidth && line) {
        lines.push(line);
        line = w;
      } else {
        line = probe;
      }
    }
    if (line) lines.push(line);
    return lines;
  };

  const maxTextWidth = bitmap.width - padding * 2;
  const captionLines = wrap(captionText, maxTextWidth);
  const studentLines = studentLine ? [studentLine] : [];
  const skillLines = skillLine ? wrap(skillLine, maxTextWidth) : [];
  const totalLines = captionLines.length + studentLines.length + skillLines.length;
  const stripHeight = padding * 2 + totalLines * lineHeight;

  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height + stripHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) return photoBlob;

  // Photo
  ctx.drawImage(bitmap, 0, 0);

  // Caption strip background
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, bitmap.height, canvas.width, stripHeight);

  // Text
  ctx.font = `${fontSize}px -apple-system, "Segoe UI", Inter, sans-serif`;
  ctx.textBaseline = 'top';
  let y = bitmap.height + padding;

  if (captionLines.length > 0) {
    ctx.fillStyle = '#1f2937';
    captionLines.forEach((line) => {
      ctx.fillText(line, padding, y);
      y += lineHeight;
    });
  }
  if (studentLines.length > 0) {
    ctx.fillStyle = '#374151';
    studentLines.forEach((line) => {
      ctx.fillText(line, padding, y);
      y += lineHeight;
    });
  }
  if (skillLines.length > 0) {
    ctx.fillStyle = '#6b7280';
    ctx.font = `${Math.round(fontSize * 0.85)}px -apple-system, "Segoe UI", Inter, sans-serif`;
    skillLines.forEach((line) => {
      ctx.fillText(line, padding, y);
      y += lineHeight;
    });
  }

  return new Promise<Blob>((resolve) => {
    canvas.toBlob(
      (b) => resolve(b ?? photoBlob),
      'image/jpeg',
      0.92,
    );
  });
}

async function downloadAllPhotosWithCaptions(
  photos: PhotoWithUrl[],
  avatars: { emoji: string; name: string }[],
  language: 'en' | 'es',
): Promise<void> {
  for (let i = 0; i < photos.length; i++) {
    const photo = photos[i];
    try {
      const blob = await composePhotoWithCaption(photo, avatars, language);
      triggerDownload(blob, `lesson-photo-${i + 1}.jpg`);
      // Small delay so browsers don't throttle rapid downloads.
      await new Promise((r) => setTimeout(r, 200));
    } catch {
      // Skip failed photos silently — user can retry or re-download individually.
    }
  }
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

interface ReportArgs {
  code: string;
  language: 'en' | 'es';
  focusArea: string;
  gameTitle: string;
  avatars: { emoji: string; name: string }[];
  reflection: {
    surprises?: string;
    growth?: string;
    adjustments?: string;
  };
  photos: PhotoWithUrl[];
}

interface ArtifactInclude {
  photos: boolean;
  reflection: boolean;
  avatars: boolean;
}

function buildReportHtml(args: ReportArgs, include: ArtifactInclude): string {
  const l = (en: string, es: string) => (args.language === 'en' ? en : es);
  const when = new Date().toLocaleString(args.language === 'es' ? 'es-ES' : 'en-US');

  const photosHtml = !include.photos ? '' : args.photos
    .map((p, i) => {
      const studentEmojis = p.student_tags
        .map((idx) => args.avatars[idx]?.emoji ?? '')
        .filter(Boolean)
        .join(' ');
      const skillChips = p.skill_tags
        .map((s) => {
          const c = SKILL_TAG_COLORS[s];
          const label = SKILL_TAG_LABELS[s][args.language];
          return `<span style="display:inline-block;padding:2px 8px;border-radius:999px;background:${c.bg};color:${c.fg};font-size:11px;font-weight:600;margin-right:4px">${escapeHtml(label)}</span>`;
        })
        .join('');
      return `
        <div style="border:1px solid #e5e7eb;border-radius:12px;overflow:hidden;margin-bottom:16px;break-inside:avoid">
          <img src="${p.publicUrl}" alt="Photo ${i + 1}" style="width:100%;max-height:420px;object-fit:cover;display:block"/>
          <div style="padding:12px">
            ${p.caption ? `<p style="margin:0 0 8px;font-size:14px;color:#374151">${escapeHtml(p.caption)}</p>` : ''}
            ${studentEmojis ? `<p style="margin:0 0 6px;font-size:20px;line-height:1">${studentEmojis}</p>` : ''}
            ${skillChips ? `<div>${skillChips}</div>` : ''}
          </div>
        </div>`;
    })
    .join('');

  const reflection = include.reflection ? (args.reflection ?? {}) : {};
  const reflectionBlock = [
    reflection.surprises &&
      `<h3 style="margin:16px 0 4px;color:#065f46;font-size:14px">${l('What surprised you today?', '¿Qué te sorprendió hoy?')}</h3><p style="margin:0 0 8px">${escapeHtml(reflection.surprises)}</p>`,
    reflection.growth &&
      `<h3 style="margin:16px 0 4px;color:#065f46;font-size:14px">${l('Which students showed growth?', '¿Qué estudiantes mostraron crecimiento?')}</h3><p style="margin:0 0 8px">${escapeHtml(reflection.growth)}</p>`,
    reflection.adjustments &&
      `<h3 style="margin:16px 0 4px;color:#065f46;font-size:14px">${l('What would you adjust for next time?', '¿Qué ajustarías para la próxima vez?')}</h3><p style="margin:0 0 8px">${escapeHtml(reflection.adjustments)}</p>`,
  ]
    .filter(Boolean)
    .join('');

  const html = `<!doctype html>
<html lang="${args.language}">
<head>
<meta charset="utf-8">
<title>Wonderpath — ${escapeHtml(args.gameTitle)}</title>
<style>
  body { font-family: -apple-system, Inter, sans-serif; max-width: 840px; margin: 0 auto; padding: 24px; color: #1f2937; }
  h1 { font-size: 28px; color: #2a6d5b; margin: 0 0 4px; }
  h2 { font-size: 18px; color: #374151; margin: 24px 0 8px; border-bottom: 1px solid #e5e7eb; padding-bottom: 4px; }
  .meta { color: #6b7280; font-size: 13px; }
  .code { font-family: ui-monospace, 'SF Mono', monospace; background: #f3f4f6; padding: 2px 6px; border-radius: 4px; }
</style>
</head>
<body>
  <h1>${l('Lesson report', 'Informe de lección')} — ${escapeHtml(args.gameTitle)}</h1>
  <p class="meta">
    ${l('Focus', 'Enfoque')}: ${escapeHtml(args.focusArea)} ·
    ${l('Generated', 'Generado')}: ${escapeHtml(when)} ·
    ${l('Session', 'Sesión')}: <span class="code">${escapeHtml(args.code)}</span>
  </p>

  ${include.avatars && args.avatars.length > 0
    ? `<h2>${l('Students', 'Estudiantes')} (${args.avatars.length})</h2>
       <p style="font-size:22px;line-height:1.3;margin:0">${args.avatars.map((a) => `${a.emoji}${a.name ? ` ${escapeHtml(a.name)}` : ''}`).join(' &nbsp; ')}</p>`
    : ''}

  ${reflectionBlock ? `<h2>${l('Teacher reflection', 'Reflexión del maestro')}</h2>${reflectionBlock}` : ''}

  ${include.photos && args.photos.length > 0
    ? `<h2>${l('Photos', 'Fotos')} (${args.photos.length})</h2>${photosHtml}`
    : ''}
</body>
</html>`;

  return html;
}

/**
 * Open the report in a hidden iframe and trigger print. Modern browsers default
 * the print destination to "Save as PDF", so the user gets a one-confirm PDF
 * download. No PDF-generation dependency required.
 *
 * Caveat: photo `<img>` tags reference Supabase public URLs — they need a
 * moment to fetch before print() fires, otherwise blank boxes appear in the PDF.
 * We wait for the iframe's load event AND any nested image loads before printing.
 */
function downloadPdfReport(args: ReportArgs, include: ArtifactInclude): void {
  const html = buildReportHtml(args, include);
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  document.body.appendChild(iframe);

  const cleanup = () => {
    setTimeout(() => iframe.remove(), 1000);
  };

  iframe.addEventListener('load', () => {
    const win = iframe.contentWindow;
    const doc = iframe.contentDocument;
    if (!win || !doc) {
      cleanup();
      return;
    }
    // Wait for images to settle so the PDF doesn't snapshot blank thumbnails.
    const imgs = Array.from(doc.images || []);
    const ready = imgs.length === 0
      ? Promise.resolve()
      : Promise.all(
          imgs.map(
            (img) =>
              new Promise<void>((resolve) => {
                if (img.complete) return resolve();
                img.addEventListener('load', () => resolve(), { once: true });
                img.addEventListener('error', () => resolve(), { once: true });
              }),
          ),
        );
    void ready.then(() => {
      try {
        win.focus();
        win.print();
      } finally {
        cleanup();
      }
    });
  });

  // srcdoc is the simplest cross-browser way to load arbitrary HTML.
  iframe.srcdoc = html;
}

interface TeachingFacilitationProps {
  onBack: () => void;
}

type Phase = 'welcome' | 'focus-selection' | 'custom-intake' | 'avatar-assignment' | 'lesson-preview' | 'in-session' | 'reflection' | 'complete';

interface FocusArea {
  id: string;
  name: { en: string; es: string };
  /** Game module for the in-session activity. `undefined` for co-created
   *  custom focus areas — those run on lesson phases + prompts only. */
  game?: string;
  gameTitle: { en: string; es: string };
}

interface LessonPhase {
  id: number;
  name: { en: string; es: string };
  duration: number;
  prompts: { en: string[]; es: string[] };
  /**
   * Plain-language description of what the step is and how to lead it.
   * Seeded from the Wonderpath Little Teaching Pack so teachers have a
   * starting point. Fully editable per session — the in-session prompt
   * header surfaces it as italic-serif body copy so the teacher always
   * has the "why" in view alongside the cue prompts.
   */
  description?: { en: string; es: string };
}

interface StudentAvatar {
  emoji: string;
  name: string;
}

const CREATURE_AVATARS = [
  '🐝', '🐛', '🦋', '🐞', '🐌', '🦗', '🕷️', '🐜',
  '🦎', '🐸', '🐢', '🦆', '🐤', '🦜', '🦉', '🦅',
  '🐿️', '🦔', '🐇', '🦇', '🐁', '🦊', '🦝', '🐻'
];

const FOCUS_AREAS: FocusArea[] = [
  {
    id: 'literacy',
    name: { en: 'Develop Early Literacy', es: 'Desarrollar Lectoescritura Temprana' },
    game: 'animal-abcs',
    gameTitle: { en: 'Animal ABCs, 123s', es: 'ABC de Animales, 123' }
  },
  {
    id: 'mindfulness',
    name: { en: 'Practice Breathing Mindfulness', es: 'Practicar Atención Plena Respiratoria' },
    game: 'pepe-breathing',
    gameTitle: { en: 'Pepe Breathing', es: 'Respiración de Pepe' }
  },
  {
    id: 'imagination',
    name: { en: 'Draw Imagination', es: 'Dibujar Imaginación' },
    game: 'peke-story',
    gameTitle: { en: 'Peke Story Sequence', es: 'Secuencia de Historia de Peke' }
  },
  {
    id: 'expression',
    name: { en: 'Express Ideas and Feelings', es: 'Expresar Ideas y Sentimientos' },
    game: 'superpowers',
    gameTitle: { en: 'Creature Superpowers', es: 'Superpoderes de Criaturas' }
  }
];

const LESSON_PHASES: LessonPhase[] = [
  {
    id: 1,
    name: { en: 'Gathering and sensing', es: 'Reunión y percepción' },
    duration: 5,
    description: {
      en: 'Children sit in a circle. Invite them to close their eyes and notice what they feel — in their body, in the air, on their skin. Then ask what they noticed on their way here. Accept all answers without evaluation; reflect each one back without interpretation. ELL: offer a visual sense map — children can point without speaking.',
      es: 'Los niños se sientan en círculo. Invítalos a cerrar los ojos y notar lo que sienten — en el cuerpo, en el aire, en la piel. Luego pregunta qué notaron de camino aquí. Acepta todas las respuestas sin evaluar; refleja cada una sin interpretar. Para estudiantes de inglés: ofrece un mapa visual de sentidos — pueden señalar sin hablar.',
    },
    prompts: {
      en: [
        'Welcome students and have them sit in a circle',
        'Guide them through a calming breath exercise',
        'Ask: "What did you notice on your way here today?"'
      ],
      es: [
        'Da la bienvenida a los estudiantes y haz que se sienten en círculo',
        'Guíalos a través de un ejercicio de respiración calmante',
        'Pregunta: "¿Qué notaste en tu camino aquí hoy?"'
      ]
    }
  },
  {
    id: 2,
    name: { en: 'Learners get Avatars', es: 'Los estudiantes reciben Avatares' },
    duration: 3,
    description: {
      en: 'Hand each child one ecological avatar — a biotic creature (Fox, Bee, Fern, Heron…) or an abiotic force (River, Wind, Rain, Stone…). The child becomes that creature or force for the session; their making comes from inside that identity. Avatars replace names in all documentation, so no personal data leaves the room.',
      es: 'Entrega a cada niño un avatar ecológico — una criatura biótica (Zorro, Abeja, Helecho, Garza…) o una fuerza abiótica (Río, Viento, Lluvia, Piedra…). El niño se convierte en esa criatura o fuerza durante la sesión; su creación nace desde esa identidad. Los avatares reemplazan los nombres en toda la documentación — ningún dato personal sale del salón.',
    },
    prompts: {
      en: [
        'Show students their creature avatars',
        'Ask each child to introduce their creature',
        'Encourage them to make the sound their creature might make'
      ],
      es: [
        'Muestra a los estudiantes sus avatares de criaturas',
        'Pide a cada niño que presente su criatura',
        'Anímalos a hacer el sonido que su criatura podría hacer'
      ]
    }
  },
  {
    id: 3,
    name: { en: 'Show selected game', es: 'Mostrar juego seleccionado' },
    duration: 10,
    description: {
      en: 'Open the game on the shared screen, or narrate it from memory if no projector is available. Pause to wonder together: "What does this creature feel? What does its home need?" Ask wonder questions, not comprehension questions. End with: "If you could bring something from this story into your hands — what would it be?"',
      es: 'Abre el juego en la pantalla compartida, o nárralo de memoria si no hay proyector. Hagan pausas para preguntarse juntos: "¿Qué siente esta criatura? ¿Qué necesita su hogar?" Haz preguntas de asombro, no de comprensión. Cierra con: "Si pudieras traer algo de esta historia a tus manos, ¿qué sería?"',
    },
    prompts: {
      en: [
        'Demonstrate the game on the shared device',
        'Invite 2-3 students to try it together',
        'Explain: "Soon you\'ll make your own creations for these animals"'
      ],
      es: [
        'Demuestra el juego en el dispositivo compartido',
        'Invita a 2-3 estudiantes a probarlo juntos',
        'Explica: "Pronto harán sus propias creaciones para estos animales"'
      ]
    }
  },
  {
    id: 4,
    name: { en: 'Transition: Find materials', es: 'Transición: Encontrar materiales' },
    duration: 2,
    description: {
      en: 'Show materials briefly — hold each one up and name it in English and Spanish. Do not show a sample. Do not suggest what to make. Give one invitation: "Go find what calls to you." The work must come entirely from the child.',
      es: 'Muestra los materiales brevemente — sostén cada uno y nómbralo en inglés y español. No muestres un ejemplo. No sugieras qué hacer. Da una sola invitación: "Ve a buscar lo que te llame." La obra debe nacer completamente del niño.',
    },
    prompts: {
      en: [
        '"We\'ll make something for your avatars and the creatures we played with"',
        '"Go find materials you want to use"',
        'Set clear boundaries for material gathering'
      ],
      es: [
        '"Haremos algo para tus avatares y las criaturas con las que jugamos"',
        '"Ve a buscar materiales que quieras usar"',
        'Establece límites claros para recoger materiales'
      ]
    }
  },
  {
    id: 5,
    name: { en: 'Free work with facilitation', es: 'Trabajo libre con facilitación' },
    duration: 20,
    description: {
      en: 'Children work freely. Circulate slowly using the four facilitation moves: Observe 30 seconds → Describe what IS happening → Mirror their gesture → Relate to the living world → Praise one specific thing → move on. Don\'t redirect. Capture process, not product.',
      es: 'Los niños trabajan libremente. Circula despacio usando los cuatro movimientos de facilitación: Observa 30 segundos → Describe lo que ESTÁ pasando → Imita su gesto → Conecta con el mundo vivo → Elogia algo específico → continúa. No redirijas. Captura el proceso, no el producto.',
    },
    prompts: {
      en: [
        'DESCRIBE: "I see you..."',
        'PRAISE: "I love how you..."',
        'REFLECT: Mirror their actions',
        'RELATE: "I\'ll add..."',
        'CARE: Notice care for living things'
      ],
      es: [
        'DESCRIBIR: "Veo que tú..."',
        'ELOGIAR: "Me encanta cómo..."',
        'REFLEJAR: Refleja sus acciones',
        'RELACIONAR: "Yo añado..."',
        'CUIDAR: Nota el cuidado por los seres vivos'
      ]
    }
  },
  {
    id: 6,
    name: { en: 'Document creations', es: 'Documentar creaciones' },
    duration: 5,
    description: {
      en: 'Photograph each work tagged to the child\'s avatar — never their name. Take 3–5 photos that capture the process, not just the final object. The rubric on screen is for your reference only — write notes on paper. No assessment data is stored.',
      es: 'Fotografía cada obra etiquetada con el avatar del niño — nunca con su nombre. Toma 3–5 fotos que capturen el proceso, no solo el objeto final. La rúbrica en pantalla es solo para tu referencia — escribe las notas en papel. No se guarda ningún dato de evaluación.',
    },
    prompts: {
      en: [
        'Take 3-5 photos of each child\'s work',
        'Capture the process, not just the final product',
        'Ask: "Tell me about what you made"'
      ],
      es: [
        'Toma 3-5 fotos del trabajo de cada niño',
        'Captura el proceso, no solo el producto final',
        'Pregunta: "Cuéntame sobre lo que hiciste"'
      ]
    }
  },
  {
    id: 7,
    name: { en: 'Sharing cycle', es: 'Ciclo de compartir' },
    duration: 10,
    description: {
      en: 'Children return to the circle, with or without their creation. Invite — never require — one word or gesture about how making felt. Respond with specific praise: "I love how the River wrapped the yarn like water moving around a stone." Close together: "Care for all life — everything thrives."',
      es: 'Los niños regresan al círculo, con o sin su creación. Invita — nunca exijas — una palabra o un gesto sobre cómo se sintió crear. Responde con un elogio específico: "Me encanta cómo el Río envolvió el hilo como el agua que rodea una piedra." Cierren juntos: "Cuida toda la vida — todo florece."',
    },
    prompts: {
      en: [
        'Invite each child to share their creation',
        'Ask the group: "What do you notice about [name]\'s work?"',
        'Celebrate all attempts and ideas'
      ],
      es: [
        'Invita a cada niño a compartir su creación',
        'Pregunta al grupo: "¿Qué notas sobre el trabajo de [nombre]?"',
        'Celebra todos los intentos e ideas'
      ]
    }
  },
  {
    id: 8,
    name: { en: 'Teacher reflection', es: 'Reflexión del maestro' },
    duration: 5,
    description: {
      en: 'A short solo reflection within 24 hours. Which avatar surprised you, and what did you learn from watching it? When did you want to redirect a child — and did you hold back? What would you change in the first 5 minutes next time? Write your notes, then create an artifact to share with families in avatar language (no child names).',
      es: 'Una breve reflexión a solas dentro de 24 horas. ¿Qué avatar te sorprendió y qué aprendiste al observarlo? ¿Cuándo quisiste redirigir a un niño — y lograste contenerte? ¿Qué cambiarías en los primeros 5 minutos la próxima vez? Escribe tus notas y luego crea un artefacto para compartir con las familias en lenguaje de avatares (sin nombres de niños).',
    },
    prompts: {
      en: [
        'What surprised you today?',
        'Which students showed growth?',
        'What would you adjust for next time?',
        'Next: write your notes, then create the artifact to share.'
      ],
      es: [
        '¿Qué te sorprendió hoy?',
        '¿Qué estudiantes mostraron crecimiento?',
        '¿Qué ajustarías para la próxima vez?',
        'Siguiente: escribe tus notas y luego crea el artefacto para compartir.'
      ]
    }
  }
];

/**
 * Suggested phase templates surfaced in the lesson-flow editor. Drawn from
 * the Wonderpath Guide + WonderGuidance methodology (the four teacher
 * moves + care for all life). When a teacher inserts one, it gets a fresh
 * id and the prompts below seed the in-session prompt cards.
 *
 * TODO: replace this static list with personalized suggestions from the
 * /api/wonderguide endpoint once the AI assistant is wired in.
 */
type SuggestedPhase = Omit<LessonPhase, 'id'>;

const SUGGESTED_PHASES: SuggestedPhase[] = [
  {
    name: { en: 'Body warm-up', es: 'Calentamiento del cuerpo' },
    duration: 3,
    description: {
      en: 'A 60-second movement reset before sitting work. Shake out hands, roll shoulders, walk on tippy-toes. Each child can add one move and the class mirrors it. Returns the body to the room before deeper attention.',
      es: 'Un reinicio de movimiento de 60 segundos antes del trabajo sentado. Sacude las manos, rueda los hombros, camina en puntitas. Cada niño/a puede agregar un movimiento y la clase lo imita. Devuelve el cuerpo al salón antes de la atención profunda.',
    },
    prompts: {
      en: [
        'Lead a 60-second movement: shake out hands, roll shoulders, tippy-toes.',
        'Invite each child to add one move; the class follows.',
        'Settle back into a circle with three slow breaths.',
      ],
      es: [
        'Guía un minuto de movimiento: sacude las manos, rueda los hombros, en puntitas.',
        'Invita a cada niño/a a agregar un movimiento; la clase lo sigue.',
        'Vuelve al círculo con tres respiraciones lentas.',
      ],
    },
  },
  {
    name: { en: 'Sensory exploration', es: 'Exploración sensorial' },
    duration: 5,
    description: {
      en: 'Pass around a natural object — a leaf, a pinecone, a stone. Each child names one thing they notice (texture, smell, weight) before passing it on. Anchors attention through the body and primes ecological wonder before any making begins.',
      es: 'Pasa un objeto natural — una hoja, una piña, una piedra. Cada niño/a nombra algo que nota (textura, olor, peso) antes de pasarlo. Ancla la atención a través del cuerpo y prepara el asombro ecológico antes de que empiece la creación.',
    },
    prompts: {
      en: [
        'Pass around a natural object — a leaf, a pinecone, a stone.',
        'Each child names one thing they notice (texture, smell, weight).',
        'Reflect together: how is this different from things we usually touch?',
      ],
      es: [
        'Pasa un objeto natural — una hoja, una piña, una piedra.',
        'Cada niño/a nombra algo que nota (textura, olor, peso).',
        'Reflexionen juntos: ¿en qué se diferencia de las cosas que solemos tocar?',
      ],
    },
  },
  {
    name: { en: 'Quiet reflection', es: 'Reflexión en silencio' },
    duration: 3,
    description: {
      en: 'Solo time after a noisy activity. Each child finds a comfortable spot alone. Set a soft chime for 2 minutes. When it rings, regather; sharing afterward is invited but optional. Trains nervous-system regulation alongside attention.',
      es: 'Tiempo a solas después de una actividad ruidosa. Cada niño/a encuentra un lugar cómodo a solas. Pon un sonido suave por 2 minutos. Cuando suene, reúnanse; compartir después es opcional. Entrena la regulación del sistema nervioso junto con la atención.',
    },
    prompts: {
      en: [
        'Invite each child to find a comfortable spot alone.',
        'Set a soft chime or quiet timer for 2 minutes.',
        'When the chime rings, gather and share one thought (optional).',
      ],
      es: [
        'Invita a cada niño/a a encontrar un lugar cómodo a solas.',
        'Pon un sonido suave o temporizador silencioso por 2 minutos.',
        'Cuando suene, reúnanse y compartan un pensamiento (opcional).',
      ],
    },
  },
  {
    name: { en: 'Story listening', es: 'Escucha de historia' },
    duration: 6,
    description: {
      en: 'Read aloud a short story tied to today\'s focus area. Pause once to ask: "What do you think happens next?" — then keep reading. Closing question is character-focused, not plot-focused: "What did you notice about the characters?"',
      es: 'Lee en voz alta un cuento corto ligado al área de enfoque de hoy. Haz una pausa para preguntar: "¿Qué crees que pasa después?" — luego sigue leyendo. La pregunta de cierre se enfoca en los personajes, no en la trama: "¿Qué notaron sobre los personajes?"',
    },
    prompts: {
      en: [
        'Read aloud a short story tied to today\'s focus area.',
        'Pause once to ask: "What do you think happens next?"',
        'After reading, ask: "What did you notice about the characters?"',
      ],
      es: [
        'Lee en voz alta un cuento corto ligado al área de enfoque de hoy.',
        'Haz una pausa para preguntar: "¿Qué crees que pasa después?"',
        'Al terminar, pregunta: "¿Qué notaron sobre los personajes?"',
      ],
    },
  },
  {
    name: { en: 'Closing circle', es: 'Círculo de cierre' },
    duration: 5,
    description: {
      en: 'Gather one final time. Each child offers one word from today (any language). The teacher names something specific they noticed in each child. End with a shared ritual — a breath together, a clap, a wave — and the closing line: "Care for all life — everything thrives."',
      es: 'Reúnanse una última vez. Cada niño/a ofrece una palabra del día (en cualquier idioma). La maestra/o nombra algo específico que notó en cada niño/a. Cierren con un ritual compartido — una respiración juntos, un aplauso, un saludo — y la frase: "Cuida toda la vida — todo florece."',
    },
    prompts: {
      en: [
        'Gather in a circle. Each child shares one word from today.',
        'Praise something specific you noticed in each child today.',
        'End with a shared breath or a small ritual (clap, wave, bow).',
      ],
      es: [
        'Reúnanse en círculo. Cada niño/a comparte una palabra del día.',
        'Reconoce algo específico que notaste en cada niño/a hoy.',
        'Cierren con una respiración compartida o un pequeño ritual.',
      ],
    },
  },
];

// ── Shared design tokens — same vocabulary as the homepage and games ───
const TF_INK = '#2a6d5b';            // Primary heading / body ink (deep green)
const TF_INK_SOFT = '#355c46';       // Secondary text
const TF_BRAND_GREEN = '#42a57a';    // Primary CTA / focus accent
const TF_GOLD = '#c9a961';           // Wonderpath gold — used as warm hairline accent
const TF_CREAM = '#fbfbe8';          // App body background
const TF_CREAM_DEEP = '#f4efde';     // Cream chip / soft surface
const TF_CARD_BORDER = '#e8e0c8';    // White-card hairline border
const TF_MUTED = '#9a9a9a';          // Microcopy
const TF_SOFT_RED = '#b85a55';       // Destructive / remove
const TF_SERIF = "'Fraunces', Georgia, serif";
const TF_SANS = "'Inter', system-ui, -apple-system, sans-serif";

/** White surface with the cream hairline border — used for every content card. */
const tfCard: React.CSSProperties = {
  background: '#ffffff',
  border: `1px solid ${TF_CARD_BORDER}`,
  borderRadius: 24,
};

/** Section heading — Fraunces 300, ink-green, large. */
const tfH2: React.CSSProperties = {
  fontFamily: TF_SERIF,
  fontWeight: 300,
  fontSize: 'clamp(30px, 3.8vw, 44px)',
  color: TF_INK,
  letterSpacing: '-0.01em',
  lineHeight: 1.1,
};

/** Italic-serif body line — used for prompts and supporting copy. */
const tfBodyItalic: React.CSSProperties = {
  fontFamily: TF_SERIF,
  fontStyle: 'italic',
  fontSize: 'clamp(18px, 2vw, 22px)',
  fontWeight: 500,
  color: TF_INK,
};

/** Inter body line for forms / instructions. */
const tfBody: React.CSSProperties = {
  fontFamily: TF_SANS,
  fontSize: 20,
  fontWeight: 400,
  color: TF_INK_SOFT,
  lineHeight: 1.55,
};

/** Uppercase Inter eyebrow / label. */
const tfEyebrow: React.CSSProperties = {
  fontFamily: TF_SANS,
  fontSize: 15,
  fontWeight: 600,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  color: TF_MUTED,
};

/** Brand-green primary pill. */
const tfPrimaryPill: React.CSSProperties = {
  background: TF_BRAND_GREEN,
  color: '#ffffff',
  fontFamily: TF_SANS,
  fontSize: 21,
  fontWeight: 600,
  padding: '14px 30px',
  borderRadius: 999,
  border: 'none',
  cursor: 'pointer',
  boxShadow: '0 6px 22px rgba(66,165,122,0.28)',
};

/** Square stepper button used by the duration editor (-/+). */
const stepperBtn: React.CSSProperties = {
  width: 38,
  height: 38,
  borderRadius: 10,
  background: '#ffffff',
  border: `1px solid ${TF_INK}25`,
  color: TF_INK,
  fontFamily: TF_SERIF,
  fontWeight: 600,
  fontSize: 22,
  lineHeight: 1,
  cursor: 'pointer',
};

/** Small round icon button — used for reorder/edit/remove + suggestions toggle. */
const iconBtn = (enabled: boolean, color: string = TF_INK): React.CSSProperties => ({
  width: 34,
  height: 34,
  borderRadius: '50%',
  background: 'transparent',
  border: `1px solid ${enabled ? `${TF_INK}25` : `${TF_INK}10`}`,
  color: enabled ? color : `${TF_INK}40`,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: enabled ? 'pointer' : 'not-allowed',
  transition: 'background-color 160ms ease, border-color 160ms ease',
});

/** Outline-green secondary pill. */
const tfOutlinePill: React.CSSProperties = {
  background: 'transparent',
  color: TF_BRAND_GREEN,
  border: `1.5px solid ${TF_BRAND_GREEN}`,
  fontFamily: TF_SANS,
  fontSize: 20,
  fontWeight: 600,
  padding: '12px 26px',
  borderRadius: 999,
  cursor: 'pointer',
};

/** A single rotating "AI is thinking" stage, bilingual. */
type ProgressStage = { en: string; es: string };

const COACHING_REVIEW_STAGES: ProgressStage[] = [
  { en: 'Reading your plan…', es: 'Leyendo tu plan…' },
  { en: "Reflecting on what's strong…", es: 'Reflexionando sobre lo fuerte…' },
  { en: 'Drafting coaching invitations…', es: 'Redactando invitaciones de coaching…' },
];

const COACHING_SUGGEST_STAGES: ProgressStage[] = [
  { en: 'Reading your plan…', es: 'Leyendo tu plan…' },
  { en: 'Pulling from the Wonderpath approach…', es: 'Recurriendo al enfoque Wonderpath…' },
  { en: 'Shaping personalized ideas…', es: 'Dando forma a ideas personalizadas…' },
];

const COACHING_INTEGRATE_STAGES: ProgressStage[] = [
  { en: 'Reading your plan + coaching…', es: 'Leyendo tu plan + coaching…' },
  { en: 'Choosing where each invitation fits…', es: 'Eligiendo dónde encaja cada invitación…' },
  { en: 'Drafting the integrated steps…', es: 'Redactando los pasos integrados…' },
];

/**
 * Staged "AI is thinking" indicator. Rotates through stage copy at a
 * fixed cadence and fills a slim progress bar over `estimatedSeconds`.
 * If Claude is slower than expected, the bar holds at ~92% with a soft
 * pulse and the copy switches to "Almost ready…" so the teacher knows
 * the system is still working — they don't bail or click frantically.
 */
function CoachingProgress({
  stages,
  estimatedSeconds = 12,
  almostReady = { en: 'Almost ready…', es: 'Casi listo…' },
}: {
  stages: ProgressStage[];
  estimatedSeconds?: number;
  almostReady?: ProgressStage;
}) {
  const { language } = useLanguage();
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const start = Date.now();
    const id = setInterval(() => setElapsed((Date.now() - start) / 1000), 100);
    return () => clearInterval(id);
  }, []);

  const pastEstimated = elapsed >= estimatedSeconds;
  const stageIdx = pastEstimated
    ? stages.length - 1
    : Math.min(stages.length - 1, Math.floor((elapsed / estimatedSeconds) * stages.length));
  const currentCopy = pastEstimated ? almostReady : stages[stageIdx];
  const progressPct = Math.min(92, (elapsed / estimatedSeconds) * 92);

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        background: TF_CREAM_DEEP,
        borderRadius: 14,
        border: `1px solid ${TF_GOLD}33`,
        padding: '16px 18px',
      }}
    >
      <div className="flex items-center gap-2" style={{ marginBottom: 10 }}>
        <Sparkles
          className="w-4 h-4"
          style={{
            color: TF_BRAND_GREEN,
            animation: 'wp-spin 1.4s linear infinite',
            flexShrink: 0,
          }}
        />
        <p
          style={{
            fontFamily: TF_SERIF,
            fontStyle: 'italic',
            fontWeight: 500,
            fontSize: 17,
            color: TF_INK_SOFT,
            margin: 0,
            transition: 'opacity 240ms ease',
          }}
        >
          {currentCopy[language]}
        </p>
      </div>
      <div
        style={{
          height: 4,
          background: `${TF_INK}10`,
          borderRadius: 999,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${progressPct}%`,
            background: TF_BRAND_GREEN,
            transition: 'width 200ms linear',
            animation: pastEstimated ? 'wp-pulse 1.6s ease-in-out infinite' : undefined,
          }}
        />
      </div>
      <style>{`
        @keyframes wp-pulse {
          0%, 100% { opacity: 0.55; }
          50%      { opacity: 1; }
        }
        @keyframes wp-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

/**
 * Reflection textarea row — same reason as TeachingPhase below: lives at
 * module scope so React's component identity is stable across keystrokes.
 * When this was inlined inside the reflection-phase render, every keystroke
 * created a fresh component reference, which React treated as a brand-new
 * type — unmounting + remounting the textarea, blowing away focus, and
 * making it nearly impossible to type more than one character at a time.
 */
function ReflectionField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const { language } = useLanguage();
  const placeholder = language === 'es' ? 'Escribe tus pensamientos…' : 'Type your thoughts…';
  return (
    <div>
      <label
        className="block mb-2"
        style={{
          fontFamily: TF_SERIF,
          fontStyle: 'italic',
          fontWeight: 500,
          fontSize: 18,
          color: TF_INK,
        }}
      >
        {label}
      </label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        placeholder={placeholder}
        className="w-full focus:outline-none resize-none"
        style={{
          padding: '14px 16px',
          background: TF_CREAM,
          border: `1.5px solid ${TF_INK}33`,
          borderRadius: 14,
          fontFamily: TF_SERIF,
          fontStyle: 'italic',
          fontWeight: 500,
          fontSize: 18,
          color: TF_INK,
          transition: 'border-color 160ms ease',
        }}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = TF_BRAND_GREEN;
        }}
        onBlur={(e) => {
          e.currentTarget.style.borderColor = `${TF_INK}33`;
        }}
      />
    </div>
  );
}

/**
 * Phase shell — cream background + the app's GlobalNav.
 *
 * Defined at module scope (NOT inside TeachingFacilitation) so its component
 * identity is stable across renders. When this was inlined inside the parent,
 * every state change (textarea keystroke, prompt advance, session sync tick)
 * produced a fresh component reference, which React treated as a different
 * component type and unmounted/remounted the entire phase tree on every
 * keystroke — replaying entry animations and causing the visible flicker.
 */
function TeachingPhase({
  onBack,
  children,
}: {
  onBack: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      style={{ background: TF_CREAM }}
    >
      <GlobalNav onBackToHome={onBack} />
      <div className="p-4 md:p-8">{children}</div>
    </div>
  );
}

export function TeachingFacilitation({ onBack }: TeachingFacilitationProps) {
  const { language } = useLanguage();
  const session = useTeachingSession();

  const [phase, setPhase] = useState<Phase>('welcome');
  const [selectedFocus, setSelectedFocus] = useState<FocusArea | null>(null);
  const [studentAvatars, setStudentAvatars] = useState<StudentAvatar[]>([]);
  const [currentLessonPhase, setCurrentLessonPhase] = useState(0);
  const [hasDevice, setHasDevice] = useState<boolean | null>(null);
  /** Editable copy of LESSON_PHASES — the user can reorder, rename, change
      duration, remove phases, and insert AI-suggested ones during the
      lesson-preview phase. The default mirrors LESSON_PHASES exactly. */
  const [lessonPhases, setLessonPhases] = useState<LessonPhase[]>(LESSON_PHASES);
  /** Phase id currently being inline-edited in the preview (name + duration + description). */
  const [editingPhaseId, setEditingPhaseId] = useState<number | null>(null);
  /** Phase ids whose description is expanded for read-only preview. */
  const [expandedDescriptionIds, setExpandedDescriptionIds] = useState<Set<number>>(
    () => new Set(),
  );
  /** Toggles the AI-suggestions tray below the timeline. */
  const [showSuggestions, setShowSuggestions] = useState(false);
  /** Toggles the hands-on play activities tray. */
  const [showPlayIdeas, setShowPlayIdeas] = useState(false);
  /** Filter for the play activities tray. null = show all. */
  const [playFilter, setPlayFilter] = useState<PlayCategory | null>(null);
  /** Activity ids whose details are expanded inline (materials + vocabulary). */
  const [expandedActivityIds, setExpandedActivityIds] = useState<Set<string>>(
    () => new Set(),
  );
  /** Personalized AI-fetched suggestions; null = haven't fetched yet (or
      fetch failed); we fall back to the static SUGGESTED_PHASES list. */
  const [aiSuggestions, setAiSuggestions] = useState<SuggestedPhase[] | null>(null);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suggestionsError, setSuggestionsError] = useState<string | null>(null);
  /** Inline coach review panel (Stage 3). */
  type ReviewPayload = {
    strengths: string[];
    considerations: string[];
    pillars: Array<{ id: string; title: { en: string; es: string }; description: { en: string; es: string } }>;
  };
  const [reviewPanel, setReviewPanel] = useState<ReviewPayload | null>(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);

  // ── Co-create your focus area (custom intake → AI compose) ──
  // The teacher describes a focus that isn't covered by an existing
  // module; we send it to the lesson coach's `compose` action and
  // hydrate `lessonPhases` from the response, then drop them into the
  // existing avatar-assignment → lesson-preview pipeline.
  const [customFocusText, setCustomFocusText] = useState('');
  const [customEmphasisSkillIds, setCustomEmphasisSkillIds] = useState<Set<string>>(
    () => new Set(),
  );
  /** Optional app-feature ids the teacher wants the AI to weave in. */
  const [customFeatureIds, setCustomFeatureIds] = useState<Set<string>>(() => new Set());
  const [composerLoading, setComposerLoading] = useState(false);
  const [composerError, setComposerError] = useState<string | null>(null);

  // ── Integration proposal (the "Integrate coaching into my plan" flow) ──
  // The server returns structured changes (insert + modify) anchored to
  // specific phase indices. We hold them as an *ephemeral* proposal:
  // nothing touches `lessonPhases` until the teacher hits Apply.
  type IntegrationInsert = {
    kind: 'insert';
    after: number;
    phase: {
      name: { en: string; es: string };
      duration: number;
      description: { en: string; es: string };
      prompts: { en: string[]; es: string[] };
      pillarId?: string;
    };
    rationale: { en: string; es: string };
    pillarId?: string;
  };
  type IntegrationModify = {
    kind: 'modify';
    target: number;
    patch: {
      name?: { en: string; es: string };
      description?: { en: string; es: string };
      duration?: number;
    };
    rationale: { en: string; es: string };
    pillarId?: string;
  };
  type IntegrationChange = IntegrationInsert | IntegrationModify;
  type IntegrationPillar = {
    id: string;
    title: { en: string; es: string };
    description: { en: string; es: string };
  };
  type IntegrationProposal = {
    changes: IntegrationChange[];
    pillars: IntegrationPillar[];
  };
  /** Per-change inline-edit override (applied on Apply if present). */
  type ChangeEdit = {
    name?: { en: string; es: string };
    description?: { en: string; es: string };
    duration?: number;
  };

  const [integrationProposal, setIntegrationProposal] =
    useState<IntegrationProposal | null>(null);
  const [integrationLoading, setIntegrationLoading] = useState(false);
  const [integrationError, setIntegrationError] = useState<string | null>(null);
  /** Distinct from `integrationError`: the call succeeded but the coach
   *  proposed zero structural changes. We render this as a celebratory
   *  "your plan reads clean" note, not as a red failure. */
  const [integrationEmpty, setIntegrationEmpty] = useState(false);
  /** Indexes (within proposal.changes) that the teacher has accepted. */
  const [acceptedChangeIds, setAcceptedChangeIds] = useState<Set<number>>(
    () => new Set(),
  );
  /** Inline edits keyed by change index. */
  const [changeEdits, setChangeEdits] = useState<Record<number, ChangeEdit>>({});
  /** Which change cards are currently expanded into the inline edit form. */
  const [editingChangeIds, setEditingChangeIds] = useState<Set<number>>(
    () => new Set(),
  );
  /** Index of the assigned avatar whose fun fact is revealed (for sharing
      with the child at the moment of avatar reveal). */
  const [revealedFactIndex, setRevealedFactIndex] = useState<number | null>(null);
  const [reflectionNotes, setReflectionNotes] = useState<ReflectionNotes>({});
  const [currentPromptIndex, setCurrentPromptIndex] = useState(0);

  // Artifact picker state (Complete phase)
  const [artifactFormat, setArtifactFormat] = useState<'pdf' | 'images'>('pdf');
  const [artifactInclude, setArtifactInclude] = useState<ArtifactInclude>({
    photos: true,
    reflection: true,
    avatars: true,
  });

  /** Prevents initial hydration from re-patching the server right back. */
  const hydratedRef = useRef(false);

  const t = (en: string, es: string) => (language === 'en' ? en : es);

  // ── Load session → hydrate local state ─────────────────────────────────────
  // Fires when session.session transitions from null to a row (create or resume).
  useEffect(() => {
    if (!session.session) return;
    if (hydratedRef.current && session.session.code === session.code) return;
    hydratedRef.current = true;

    const row = session.session;
    if (row.focus_area) {
      const focus = FOCUS_AREAS.find((f) => f.id === row.focus_area) ?? null;
      setSelectedFocus(focus);
    }
    setStudentAvatars(
      (row.avatars ?? []).map((a) => ({ emoji: a.emoji, name: a.name ?? '' })),
    );
    setReflectionNotes(row.reflection_notes ?? {});
    setCurrentLessonPhase(row.current_lesson_phase ?? 0);
    setPhase((row.current_phase as Phase) || 'focus-selection');
  }, [session.session, session.code]);

  // ── Sync local state → server (debounced in the hook) ──────────────────────
  useEffect(() => {
    if (!session.code || !hydratedRef.current) return;
    session.patch({ current_phase: phase });
  }, [phase, session.code]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!session.code || !hydratedRef.current) return;
    session.patch({ current_lesson_phase: currentLessonPhase });
  }, [currentLessonPhase, session.code]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!session.code || !hydratedRef.current) return;
    const mapped: StudentAvatarRecord[] = studentAvatars.map((a, i) => ({
      index: i,
      emoji: a.emoji,
      name: a.name || undefined,
    }));
    session.patch({ avatars: mapped });
  }, [studentAvatars, session.code]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!session.code || !hydratedRef.current) return;
    session.patch({ reflection_notes: reflectionNotes });
  }, [reflectionNotes, session.code]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!session.code || !hydratedRef.current) return;
    session.patch({
      focus_area: selectedFocus?.id ?? null,
      game_title: selectedFocus?.gameTitle.en ?? null,
    });
  }, [selectedFocus, session.code]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Session lifecycle ──────────────────────────────────────────────────────
  const handleStartNew = async () => {
    try {
      hydratedRef.current = true; // we're seeding state, not loading it
      await session.startNew({
        language,
        current_phase: 'focus-selection',
        current_lesson_phase: 0,
      });
      setPhase('focus-selection');
    } catch {
      /* error surfaced via session.loadError */
    }
  };

  const handleResume = async (code: string) => {
    hydratedRef.current = false;
    const ok = await session.resume(code);
    return ok;
  };

  // ── Avatar helpers ─────────────────────────────────────────────────────────
  const handleAddAvatar = (emoji: string) => {
    if (studentAvatars.length < 24) {
      setStudentAvatars([...studentAvatars, { emoji, name: '' }]);
    }
  };

  const handleUpdateAvatarName = (index: number, name: string) => {
    const updated = [...studentAvatars];
    updated[index].name = name;
    setStudentAvatars(updated);
  };

  const handleRemoveAvatar = (index: number) => {
    setStudentAvatars(studentAvatars.filter((_, i) => i !== index));
  };

  // ── Photo helpers (thin adapters over the hook) ────────────────────────────
  const togglePhotoStudentTag = (photoId: string, studentIndex: number) => {
    const photo = session.photos.find((p) => p.id === photoId);
    if (!photo) return;
    const tagged = photo.student_tags.includes(studentIndex);
    const nextTags = tagged
      ? photo.student_tags.filter((i) => i !== studentIndex)
      : [...photo.student_tags, studentIndex];
    session.patchPhoto(photoId, { student_tags: nextTags });
  };

  const startLessonPhase = (phaseIndex: number) => {
    setCurrentLessonPhase(phaseIndex);
    setCurrentPromptIndex(0);
  };

  const advanceToNextPhase = () => {
    if (currentLessonPhase < lessonPhases.length - 1) {
      startLessonPhase(currentLessonPhase + 1);
    } else {
      setPhase('reflection');
    }
  };

  const nextPrompt = () => {
    const currentPhase = lessonPhases[currentLessonPhase];
    if (!currentPhase) return;
    const prompts = currentPhase.prompts[language];
    if (currentPromptIndex < prompts.length - 1) {
      setCurrentPromptIndex(currentPromptIndex + 1);
    }
  };

  // ── Lesson-flow editor helpers ────────────────────────────────────────
  /** Swap a phase up or down in the order. No-op at the boundaries. */
  const movePhase = (idx: number, direction: -1 | 1) => {
    setLessonPhases((prev) => {
      const target = idx + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  /** Remove a phase by id. Closes its inline editor if it was open. */
  const removeLessonPhase = (id: number) => {
    setLessonPhases((prev) => prev.filter((p) => p.id !== id));
    setEditingPhaseId((cur) => (cur === id ? null : cur));
  };

  /** Patch name / duration on a phase by id. */
  const updateLessonPhase = (id: number, patch: Partial<LessonPhase>) => {
    setLessonPhases((prev) =>
      prev.map((p) => (p.id === id ? { ...p, ...patch } : p)),
    );
  };

  /** Append a suggested template as a new phase with a fresh id. */
  const addSuggestedPhase = (template: SuggestedPhase) => {
    setLessonPhases((prev) => {
      const nextId = prev.length === 0 ? 1 : Math.max(...prev.map((p) => p.id)) + 1;
      return [...prev, { ...template, id: nextId }];
    });
  };

  /**
   * Append a hands-on play activity as a new phase. The activity's
   * description includes the materials line and the vocabulary words
   * inline, so the teacher has the whole script in one block when they
   * expand the phase during class.
   */
  const addPlayActivity = (activity: PlayActivity) => {
    const buildDescription = (lang: 'en' | 'es') => {
      const parts: string[] = [activity.description[lang]];
      if (activity.materials[lang].length > 0) {
        const label = lang === 'es' ? 'Materiales' : 'Materials';
        parts.push(`${label}: ${activity.materials[lang].join(', ')}.`);
      }
      if (activity.vocabulary[lang].length > 0) {
        const label = lang === 'es' ? 'Vocabulario' : 'Vocabulary';
        parts.push(`${label}: ${activity.vocabulary[lang].join(', ')}.`);
      }
      return parts.join('\n\n');
    };
    setLessonPhases((prev) => {
      const nextId = prev.length === 0 ? 1 : Math.max(...prev.map((p) => p.id)) + 1;
      return [
        ...prev,
        {
          id: nextId,
          name: activity.name,
          duration: activity.duration,
          description: { en: buildDescription('en'), es: buildDescription('es') },
          prompts: activity.prompts,
        },
      ];
    });
  };

  // ── Lesson plan coach (AI) ──────────────────────────────────────────
  /** Build the request body shared by /suggest, /review, and /integrate. */
  const buildCoachBody = (action: 'suggest' | 'review' | 'integrate') => {
    const body: Record<string, unknown> = {
      action,
      language,
      focusArea: selectedFocus ? selectedFocus.name : { en: '', es: '' },
      gameTitle: selectedFocus ? selectedFocus.gameTitle : { en: '', es: '' },
      currentPhases: lessonPhases.map((p) => ({
        name: p.name,
        duration: p.duration,
        description: p.description,
      })),
    };
    // For integrate: pass the review's invitations so the AI converts
    // each one into a structural change instead of working from scratch.
    if (action === 'integrate' && reviewPanel?.considerations.length) {
      body.priorInvitations = reviewPanel.considerations;
    }
    return body;
  };

  /** Fetch personalized phase suggestions. Falls back to static list on error. */
  const fetchAiSuggestions = async () => {
    setSuggestionsLoading(true);
    setSuggestionsError(null);
    try {
      const res = await fetch('/api/lesson-plan-coach', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildCoachBody('suggest')),
      });
      if (!res.ok) throw new Error(`API ${res.status}`);
      const data = (await res.json()) as { suggestions?: SuggestedPhase[] };
      const next = Array.isArray(data.suggestions) && data.suggestions.length > 0 ? data.suggestions : null;
      setAiSuggestions(next);
      if (!next) {
        setSuggestionsError(
          t(
            'No personalized ideas this time — showing curated suggestions.',
            'Sin ideas personalizadas esta vez — mostrando sugerencias curadas.',
          ),
        );
      }
    } catch {
      setAiSuggestions(null);
      setSuggestionsError(
        t(
          'Could not reach WonderGuidance — showing curated suggestions.',
          'No se pudo contactar a WonderGuidance — mostrando sugerencias curadas.',
        ),
      );
    } finally {
      setSuggestionsLoading(false);
    }
  };

  /** Run the coach review on the current plan. Result lives in `reviewPanel`. */
  const fetchPlanReview = async () => {
    setReviewLoading(true);
    setReviewError(null);
    try {
      const res = await fetch('/api/lesson-plan-coach', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildCoachBody('review')),
      });
      if (!res.ok) throw new Error(`API ${res.status}`);
      const data = (await res.json()) as {
        review?: { strengths?: unknown; considerations?: unknown };
        pillars?: ReviewPayload['pillars'];
      };
      setReviewPanel({
        strengths: Array.isArray(data.review?.strengths) ? (data.review!.strengths as string[]) : [],
        considerations: Array.isArray(data.review?.considerations) ? (data.review!.considerations as string[]) : [],
        pillars: Array.isArray(data.pillars) ? data.pillars : [],
      });
    } catch {
      setReviewError(
        t(
          'Wonderpath coaching is offline right now — your plan is still saved. Try again in a moment.',
          'El coaching Wonderpath está desconectado ahora — tu plan sigue guardado. Intenta de nuevo en un momento.',
        ),
      );
    } finally {
      setReviewLoading(false);
    }
  };

  /** Ask the coach to propose structured changes against the current plan. */
  const fetchIntegrationProposal = async () => {
    setIntegrationLoading(true);
    setIntegrationError(null);
    setIntegrationEmpty(false);
    try {
      const res = await fetch('/api/lesson-plan-coach', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildCoachBody('integrate')),
      });
      if (!res.ok) throw new Error(`API ${res.status}`);
      const data = (await res.json()) as {
        integration?: { changes?: unknown };
        pillars?: IntegrationPillar[];
      };
      const changes = (Array.isArray(data.integration?.changes)
        ? data.integration!.changes
        : []) as IntegrationChange[];
      if (changes.length === 0) {
        // Successful call, nothing to integrate — celebrate, don't alarm.
        setIntegrationEmpty(true);
        return;
      }
      setIntegrationProposal({
        changes,
        pillars: Array.isArray(data.pillars) ? data.pillars : [],
      });
      // Default: every change pre-accepted. Teacher unchecks what they
      // don't want — frictionless adoption is the design intent here.
      setAcceptedChangeIds(new Set(changes.map((_, i) => i)));
      setChangeEdits({});
      setEditingChangeIds(new Set());
    } catch {
      setIntegrationError(
        t(
          'Wonderpath coaching is offline right now — your plan is still saved. Try again in a moment.',
          'El coaching Wonderpath está desconectado ahora — tu plan sigue guardado. Intenta de nuevo en un momento.',
        ),
      );
    } finally {
      setIntegrationLoading(false);
    }
  };

  /** Discard the proposal without touching the plan. */
  const cancelIntegration = () => {
    setIntegrationProposal(null);
    setAcceptedChangeIds(new Set());
    setChangeEdits({});
    setEditingChangeIds(new Set());
    setIntegrationError(null);
    setIntegrationEmpty(false);
  };

  /** Toggle whether a proposed change is accepted. */
  const toggleChangeAccepted = (i: number) => {
    setAcceptedChangeIds((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  /** Toggle inline edit form for a proposed change. */
  const toggleChangeEditing = (i: number) => {
    setEditingChangeIds((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  /** Patch the per-change edit override (used on Apply). */
  const updateChangeEdit = (i: number, partial: Partial<ChangeEdit>) => {
    setChangeEdits((prev) => ({ ...prev, [i]: { ...(prev[i] ?? {}), ...partial } }));
  };

  /**
   * Commit the accepted changes to the lesson plan.
   *
   * Modifies are applied first (no index shift). Inserts are then sorted
   * by `after` ascending and spliced in with a running offset so each
   * insert lands at the correct position relative to the *current* plan
   * (not the post-insert plan). Edited overrides win over the AI's
   * proposed values.
   */
  const applyAcceptedChanges = () => {
    if (!integrationProposal) return;
    const accepted = integrationProposal.changes
      .map((change, i) => ({ change, i }))
      .filter(({ i }) => acceptedChangeIds.has(i));
    if (accepted.length === 0) {
      cancelIntegration();
      return;
    }

    setLessonPhases((prev) => {
      const next = [...prev];
      let nextId = next.length === 0 ? 1 : Math.max(...next.map((p) => p.id)) + 1;

      // 1) Modifies — no index shift, safe to apply in any order.
      for (const { change, i } of accepted) {
        if (change.kind !== 'modify') continue;
        const target = change.target;
        if (target < 0 || target >= next.length) continue;
        const edits = changeEdits[i] ?? {};
        const existing = next[target];
        const finalName = edits.name ?? change.patch.name;
        const finalDescription = edits.description ?? change.patch.description;
        const finalDuration =
          edits.duration ?? change.patch.duration ?? existing.duration;
        next[target] = {
          ...existing,
          name: finalName ?? existing.name,
          description: finalDescription ?? existing.description,
          duration: finalDuration,
        };
      }

      // 2) Inserts — sort by `after` ascending so we walk left→right;
      //    track running offset so each splice lands at the right index.
      const inserts = accepted
        .filter(({ change }) => change.kind === 'insert')
        .map(({ change, i }) => ({
          change: change as IntegrationInsert,
          i,
        }))
        .sort((a, b) => a.change.after - b.change.after);

      let offset = 0;
      for (const { change, i } of inserts) {
        const edits = changeEdits[i] ?? {};
        const insertIdx = change.after + 1 + offset;
        next.splice(insertIdx, 0, {
          id: nextId++,
          name: edits.name ?? change.phase.name,
          duration: edits.duration ?? change.phase.duration,
          description: edits.description ?? change.phase.description,
          prompts: change.phase.prompts,
        });
        offset += 1;
      }

      return next;
    });

    // Stale review + clean proposal state.
    setReviewPanel(null);
    setIntegrationProposal(null);
    setAcceptedChangeIds(new Set());
    setChangeEdits({});
    setEditingChangeIds(new Set());
    setIntegrationError(null);
    setIntegrationEmpty(false);
  };

  /** Compose a starter session from the teacher's free-text intake. On
   *  success: synthesizes a `selectedFocus` (with `game: undefined`),
   *  hydrates `lessonPhases` from the AI response, and advances to the
   *  avatar-assignment screen. The downstream pipeline (editor, coach,
   *  in-session, reflection, complete) handles missing-game gracefully. */
  const fetchComposedPlan = async () => {
    const focusText = customFocusText.trim();
    if (focusText.length < 8) return;
    setComposerLoading(true);
    setComposerError(null);
    try {
      const body = {
        action: 'compose' as const,
        language,
        focusArea: { en: focusText, es: focusText },
        gameTitle: { en: 'Co-created session', es: 'Sesión co-creada' },
        currentPhases: [],
        composeEmphasisSkillIds: Array.from(customEmphasisSkillIds),
        composeFeatureIds: Array.from(customFeatureIds),
      };
      const res = await fetch('/api/lesson-plan-coach', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`API ${res.status}`);
      const data = (await res.json()) as {
        phases?: Array<{
          name: { en: string; es: string };
          duration: number;
          description: { en: string; es: string };
          prompts: { en: string[]; es: string[] };
        }>;
      };
      const phases = Array.isArray(data.phases) ? data.phases : [];
      if (phases.length === 0) {
        setComposerError(
          t(
            'Coaching could not draft a session this time. Try rewording your focus or adjusting the skills.',
            'El coaching no pudo redactar una sesión esta vez. Reformula tu enfoque o ajusta las habilidades.',
          ),
        );
        return;
      }
      // Hydrate the lesson plan with the AI-composed phases.
      let nextId = 1;
      setLessonPhases(
        phases.map((p) => ({
          id: nextId++,
          name: p.name,
          duration: p.duration,
          description: p.description,
          prompts: p.prompts,
        })),
      );
      // Synthesize a custom focus so the rest of the pipeline (coach
      // body, completion artifact, etc.) has a name to reference.
      setSelectedFocus({
        id: 'custom',
        name: { en: focusText, es: focusText },
        gameTitle: { en: 'Co-created session', es: 'Sesión co-creada' },
        // game is undefined — no game launcher in-session for custom.
      });
      setPhase('avatar-assignment');
    } catch {
      setComposerError(
        t(
          'Wonderpath coaching is offline right now. Try again in a moment.',
          'El coaching Wonderpath está desconectado ahora. Intenta de nuevo en un momento.',
        ),
      );
    } finally {
      setComposerLoading(false);
    }
  };

  // Welcome Phase — new/resume split
  if (phase === 'welcome') {
    return (
      <StartSessionScreen
        language={language as 'en' | 'es'}
        onBack={onBack}
        loading={session.loading}
        loadError={session.loadError}
        onStartNew={handleStartNew}
        onResume={handleResume}
      />
    );
  }

  // Tiny helper — every non-welcome phase renders the session chip if we have a code.
  const sessionChip =
    session.code && (
      <div className="flex justify-end mb-3">
        <SessionStatusChip
          code={session.code}
          syncState={session.syncState}
          language={language as 'en' | 'es'}
        />
      </div>
    );

  // Focus Selection Phase
  if (phase === 'focus-selection') {
    return (
      <TeachingPhase onBack={onBack}>
        {sessionChip && <div className="mb-4">{sessionChip}</div>}

        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-6 md:p-8 mb-6"
            style={tfCard}
          >
            <p style={{ ...tfEyebrow, marginBottom: 8 }}>
              {t('Preparation', 'Preparación')}
            </p>
            <h2 style={{ ...tfH2, marginBottom: 8 }}>
              {t('Choose your focus', 'Elige tu enfoque')}
            </h2>
            <p style={{ ...tfBodyItalic, marginBottom: 24 }}>
              {t("Let's define the Wonderpath focus area:", 'Definamos el área de enfoque de Wonderpath:')}
            </p>

            <div className="space-y-3">
              {FOCUS_AREAS.map((focus) => (
                <motion.button
                  key={focus.id}
                  onClick={() => {
                    setSelectedFocus(focus);
                    setPhase('avatar-assignment');
                  }}
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full"
                  style={{
                    background: TF_CREAM_DEEP,
                    color: TF_INK,
                    fontFamily: TF_SERIF,
                    fontStyle: 'italic',
                    fontWeight: 500,
                    fontSize: 'clamp(19px, 2.4vw, 24px)',
                    padding: '16px 22px',
                    borderRadius: 16,
                    border: `1px solid ${TF_GOLD}55`,
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'background-color 160ms ease, border-color 160ms ease',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = TF_BRAND_GREEN; e.currentTarget.style.color = '#ffffff'; e.currentTarget.style.borderColor = TF_BRAND_GREEN; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = TF_CREAM_DEEP; e.currentTarget.style.color = TF_INK; e.currentTarget.style.borderColor = `${TF_GOLD}55`; }}
                >
                  {focus.name[language]}
                </motion.button>
              ))}

              {/* Co-create custom focus — sparkle + dashed border so it
                  reads as a different *kind* of choice without breaking
                  the visual rhythm of the focus list. */}
              <motion.button
                onClick={() => {
                  setCustomFocusText('');
                  setCustomEmphasisSkillIds(new Set());
                  setCustomFeatureIds(new Set());
                  setComposerError(null);
                  setPhase('custom-intake');
                }}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                className="w-full flex items-start gap-3"
                style={{
                  background: '#ffffff',
                  color: TF_INK,
                  padding: '16px 22px',
                  borderRadius: 16,
                  border: `1.5px dashed ${TF_BRAND_GREEN}88`,
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'background-color 160ms ease, border-color 160ms ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = `${TF_BRAND_GREEN}10`;
                  e.currentTarget.style.borderColor = TF_BRAND_GREEN;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#ffffff';
                  e.currentTarget.style.borderColor = `${TF_BRAND_GREEN}88`;
                }}
              >
                <Sparkles
                  className="w-5 h-5 flex-shrink-0"
                  style={{ color: TF_BRAND_GREEN, marginTop: 6 }}
                />
                <div className="flex-1">
                  <p
                    style={{
                      fontFamily: TF_SERIF,
                      fontStyle: 'italic',
                      fontWeight: 500,
                      fontSize: 'clamp(19px, 2.4vw, 24px)',
                      color: TF_INK,
                      margin: 0,
                    }}
                  >
                    {t('Co-create your focus area', 'Co-crea tu área de enfoque')}
                  </p>
                  <p
                    style={{
                      fontFamily: TF_SANS,
                      fontWeight: 400,
                      fontSize: 14,
                      color: TF_INK_SOFT,
                      margin: '2px 0 0 0',
                    }}
                  >
                    {t(
                      'Use the Wonderpath approach for any need',
                      'Usa el enfoque Wonderpath para cualquier necesidad',
                    )}
                  </p>
                </div>
              </motion.button>
            </div>
          </motion.div>
        </div>
      </TeachingPhase>
    );
  }

  // Custom Intake Phase — co-create your focus area
  if (phase === 'custom-intake') {
    const SKILL_CHIPS: Array<{ id: string; label: { en: string; es: string } }> = [
      {
        id: 'four-facilitation-moves',
        label: {
          en: 'Describe + Praise + Connect & Reflect + Relate',
          es: 'Describir + Elogiar + Conectar y Reflejar + Relacionar',
        },
      },
      { id: 'care-for-life', label: { en: 'Care for life', es: 'Cuidar la vida' } },
      { id: 'child-led-play', label: { en: 'Child-led play', es: 'Juego liderado por el niño/a' } },
    ];
    const canCompose = customFocusText.trim().length >= 8 && !composerLoading;
    return (
      <TeachingPhase onBack={onBack}>
        <div className="mb-6 flex items-center justify-between gap-3 flex-wrap max-w-2xl mx-auto">
          <button onClick={() => setPhase('focus-selection')} style={tfOutlinePill}>
            ← {t('Back to focus areas', 'Volver a áreas de enfoque')}
          </button>
          {sessionChip}
        </div>

        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-6 md:p-8 mb-6"
            style={tfCard}
          >
            {/* Top action bar — Compose CTA stays at the top so the
                teacher always sees their commit affordance without
                scrolling. */}
            <div className="mb-5 flex items-center justify-between gap-3 flex-wrap">
              <p style={{ ...tfEyebrow, margin: 0 }}>
                {t('Co-create · Wonderpath approach', 'Co-crea · Enfoque Wonderpath')}
              </p>
              <button
                type="button"
                onClick={() => void fetchComposedPlan()}
                disabled={!canCompose}
                className="inline-flex items-center gap-2"
                style={{
                  ...tfPrimaryPill,
                  fontSize: 17,
                  padding: '10px 22px',
                  opacity: canCompose ? 1 : 0.4,
                  cursor: canCompose ? 'pointer' : 'not-allowed',
                }}
              >
                <Sparkles className="w-4 h-4" />
                {t('Compose my session →', 'Componer mi sesión →')}
              </button>
            </div>

            <h2 style={{ ...tfH2, marginBottom: 8 }}>
              {t('What do you want children to explore today?', '¿Qué quieres que los niños exploren hoy?')}
            </h2>
            <p style={{ ...tfBodyItalic, marginBottom: 18 }}>
              {t(
                'A sentence or two — Wonderpath coaching will draft a starter session you can edit, run, and refine.',
                'Una frase o dos — el coaching Wonderpath redactará una sesión inicial que puedes editar, ejecutar y refinar.',
              )}
            </p>

            <textarea
              value={customFocusText}
              onChange={(e) => setCustomFocusText(e.target.value.slice(0, 300))}
              rows={3}
              placeholder={t(
                'e.g. Wonder about birds in the schoolyard · Caring for a classroom plant · Where does our water come from? · Reuse + repair instead of throw away · Painting with mud, petals, and natural pigments · Pretending to be a tree growing through the seasons · Composting kitchen scraps to feed the soil · Following an ant\'s path through the garden · Drawing the songs we hear from birds · Building tiny shelters for snails, beetles, or worms · Recognizing emotions in ourselves and others',
                'p. ej. Asombro por los pájaros del patio · Cuidar una planta del aula · ¿De dónde viene el agua? · Reutilizar y reparar en lugar de tirar · Pintar con barro, pétalos y pigmentos naturales · Imaginar ser un árbol creciendo en cada estación · Compostar restos de comida para alimentar la tierra · Seguir el camino de una hormiga · Dibujar las canciones que cantan los pájaros · Construir refugios pequeños para caracoles, escarabajos o lombrices · Reconocer emociones en nosotros y los demás',
              )}
              className="w-full focus:outline-none resize-y"
              style={{
                padding: '14px 16px',
                background: TF_CREAM,
                border: `1.5px solid ${TF_INK}33`,
                borderRadius: 14,
                fontFamily: TF_SERIF,
                fontStyle: 'italic',
                fontWeight: 500,
                fontSize: 18,
                color: TF_INK,
                lineHeight: 1.55,
                marginBottom: 6,
                transition: 'border-color 160ms ease',
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = TF_BRAND_GREEN;
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = `${TF_INK}33`;
              }}
            />
            <p
              style={{
                fontFamily: TF_SANS,
                fontSize: 13,
                color: TF_MUTED,
                margin: '0 0 24px 0',
                textAlign: 'right',
              }}
            >
              {customFocusText.length} / 300
            </p>

            <p style={{ ...tfEyebrow, marginBottom: 10 }}>
              {t(
                'Optional · skills to emphasize',
                'Opcional · habilidades a enfatizar',
              )}
            </p>
            <p style={{ ...tfBody, marginBottom: 12 }}>
              {t(
                'Pick any that fit — coaching will weave them into the session more visibly.',
                'Elige las que encajen — el coaching las integrará a la sesión.',
              )}
            </p>
            <div className="flex flex-wrap gap-2 mb-6">
              {SKILL_CHIPS.map((chip, i) => {
                const active = customEmphasisSkillIds.has(chip.id);
                return (
                  <button
                    key={`${chip.id}-${i}`}
                    type="button"
                    onClick={() => {
                      setCustomEmphasisSkillIds((prev) => {
                        const next = new Set(prev);
                        if (next.has(chip.id)) next.delete(chip.id);
                        else next.add(chip.id);
                        return next;
                      });
                    }}
                    style={{
                      fontFamily: TF_SANS,
                      fontSize: 15,
                      fontWeight: 600,
                      color: active ? '#ffffff' : TF_INK,
                      background: active ? TF_BRAND_GREEN : '#ffffff',
                      border: `1.5px solid ${active ? TF_BRAND_GREEN : `${TF_INK}30`}`,
                      borderRadius: 999,
                      padding: '7px 14px',
                      cursor: 'pointer',
                      transition: 'all 160ms ease',
                    }}
                  >
                    {chip.label[language]}
                  </button>
                );
              })}
            </div>

            <p style={{ ...tfEyebrow, marginBottom: 10 }}>
              {t(
                'Optional · features to use',
                'Opcional · funciones a usar',
              )}
            </p>
            <p style={{ ...tfBody, marginBottom: 12 }}>
              {t(
                "Pick any to weave into the session — coaching will write the cue moments where the teacher uses them.",
                'Elige las que quieras integrar — el coaching escribirá los momentos donde se usan.',
              )}
            </p>
            <div className="flex flex-wrap gap-2 mb-6">
              {(
                [
                  { id: 'photos', label: { en: 'Photo documentation', es: 'Documentación con fotos' } },
                  { id: 'voice-notes', label: { en: 'Voice notes', es: 'Notas de voz' } },
                  { id: 'hands-on-play', label: { en: 'Hands-on play', es: 'Juego con materiales' } },
                  { id: 'voice-prompts', label: { en: 'Voice-narrated prompts', es: 'Indicaciones con voz' } },
                  { id: 'avatar-facts', label: { en: 'Avatar fun facts', es: 'Curiosidades del avatar' } },
                ] as const
              ).map((chip) => {
                const active = customFeatureIds.has(chip.id);
                return (
                  <button
                    key={chip.id}
                    type="button"
                    onClick={() => {
                      setCustomFeatureIds((prev) => {
                        const next = new Set(prev);
                        if (next.has(chip.id)) next.delete(chip.id);
                        else next.add(chip.id);
                        return next;
                      });
                    }}
                    style={{
                      fontFamily: TF_SANS,
                      fontSize: 15,
                      fontWeight: 600,
                      color: active ? '#ffffff' : TF_INK,
                      background: active ? TF_BRAND_GREEN : '#ffffff',
                      border: `1.5px solid ${active ? TF_BRAND_GREEN : `${TF_INK}30`}`,
                      borderRadius: 999,
                      padding: '7px 14px',
                      cursor: 'pointer',
                      transition: 'all 160ms ease',
                    }}
                  >
                    {chip.label[language]}
                  </button>
                );
              })}
            </div>

            {composerLoading && (
              <div className="mb-4">
                <CoachingProgress
                  stages={[
                    { en: 'Reading your focus…', es: 'Leyendo tu enfoque…' },
                    { en: 'Pulling from the Wonderpath approach…', es: 'Recurriendo al enfoque Wonderpath…' },
                    { en: 'Drafting the session…', es: 'Redactando la sesión…' },
                  ]}
                  estimatedSeconds={15}
                />
              </div>
            )}

            {!composerLoading && composerError && (
              <div
                className="flex items-start justify-between gap-3 p-3 mb-4"
                style={{
                  background: '#ffffff',
                  border: `1px solid ${TF_SOFT_RED}55`,
                  borderRadius: 12,
                }}
              >
                <p
                  style={{
                    fontFamily: TF_SERIF,
                    fontStyle: 'italic',
                    fontWeight: 500,
                    fontSize: 16,
                    color: TF_SOFT_RED,
                    margin: 0,
                    flex: 1,
                  }}
                >
                  {composerError}
                </p>
                <button
                  type="button"
                  onClick={() => setComposerError(null)}
                  aria-label={t('Dismiss', 'Cerrar')}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: TF_INK_SOFT,
                    fontSize: 18,
                    cursor: 'pointer',
                    padding: '0 4px',
                  }}
                >
                  ×
                </button>
              </div>
            )}
          </motion.div>
        </div>
      </TeachingPhase>
    );
  }

  // Avatar Assignment Phase
  if (phase === 'avatar-assignment') {
    return (
      <TeachingPhase onBack={onBack}>
        <div className="mb-6 flex items-center justify-between gap-3 flex-wrap max-w-4xl mx-auto">
          <button
            onClick={() =>
              setPhase(selectedFocus?.id === 'custom' ? 'custom-intake' : 'focus-selection')
            }
            style={tfOutlinePill}
          >
            ← {t('Back', 'Volver')}
          </button>
          {sessionChip}
        </div>

        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-6 md:p-8 mb-6"
            style={tfCard}
          >
            {/* Today's Game banner — flat brand-green with cream text accent */}
            <div
              className="mb-7 p-5"
              style={{ background: TF_BRAND_GREEN, borderRadius: 18 }}
            >
              <p style={{ ...tfEyebrow, color: 'rgba(255,255,255,0.78)', marginBottom: 6 }}>
                {t("Today's game", 'Juego de hoy')}
              </p>
              <h2
                style={{
                  fontFamily: TF_SERIF,
                  fontWeight: 300,
                  fontSize: 'clamp(26px, 3.4vw, 36px)',
                  color: '#ffffff',
                  letterSpacing: '-0.01em',
                }}
              >
                {selectedFocus?.gameTitle[language]}
              </h2>
              <p
                style={{
                  fontFamily: TF_SERIF,
                  fontStyle: 'italic',
                  fontSize: 17,
                  color: 'rgba(255,255,255,0.82)',
                  marginTop: 4,
                }}
              >
                {t('Ages 3–6', 'Edades 3–6')}
              </p>
            </div>

            <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
              <h3 style={tfH2}>
                {t('Assign creature avatars', 'Asignar avatares de criaturas')}
              </h3>
              <button onClick={() => setPhase('lesson-preview')} style={tfOutlinePill}>
                {t('Skip', 'Omitir')}
              </button>
            </div>

            {/* Assigned Avatars — each tile has a "Fun fact" toggle so the
                teacher can read a short, child-friendly fact aloud at the
                moment they hand the avatar card to the child. */}
            {studentAvatars.length > 0 && (
              <div className="mb-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {studentAvatars.map((avatar, index) => {
                  const factEntry = AVATAR_FACTS[avatar.emoji];
                  const factRevealed = revealedFactIndex === index;
                  return (
                    <div
                      key={index}
                      style={{
                        background: TF_CREAM_DEEP,
                        borderRadius: 14,
                        border: `1px solid ${TF_GOLD}33`,
                        padding: 12,
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <span style={{ fontSize: 34, lineHeight: 1 }}>{avatar.emoji}</span>
                        <input
                          type="text"
                          value={avatar.name}
                          onChange={(e) => handleUpdateAvatarName(index, e.target.value)}
                          placeholder={
                            factEntry
                              ? factEntry.name[language]
                              : t('Name / Initials', 'Nombre / Iniciales')
                          }
                          maxLength={15}
                          className="flex-1 focus:outline-none"
                          style={{
                            padding: '8px 12px',
                            background: '#ffffff',
                            border: `1px solid ${TF_CARD_BORDER}`,
                            borderRadius: 10,
                            fontFamily: TF_SANS,
                            fontSize: 17,
                            color: TF_INK,
                            transition: 'border-color 160ms ease',
                          }}
                          onFocus={(e) => { e.currentTarget.style.borderColor = TF_BRAND_GREEN; }}
                          onBlur={(e) => { e.currentTarget.style.borderColor = TF_CARD_BORDER; }}
                        />
                        {factEntry && (
                          <button
                            type="button"
                            onClick={() =>
                              setRevealedFactIndex(factRevealed ? null : index)
                            }
                            aria-pressed={factRevealed}
                            aria-label={t('Show fun fact', 'Mostrar dato curioso')}
                            title={t('Fun fact', 'Dato curioso')}
                            style={{
                              ...iconBtn(true),
                              color: factRevealed ? TF_BRAND_GREEN : TF_INK,
                              background: factRevealed ? `${TF_BRAND_GREEN}15` : 'transparent',
                              borderColor: factRevealed
                                ? TF_BRAND_GREEN
                                : `${TF_INK}25`,
                            }}
                          >
                            <Sparkles className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button
                          onClick={() => {
                            handleRemoveAvatar(index);
                            if (revealedFactIndex === index) setRevealedFactIndex(null);
                          }}
                          style={{
                            background: 'transparent',
                            color: TF_SOFT_RED,
                            border: 'none',
                            fontSize: 22,
                            fontWeight: 600,
                            cursor: 'pointer',
                            padding: '4px 8px',
                          }}
                          aria-label={t('Remove', 'Quitar')}
                        >
                          ×
                        </button>
                      </div>
                      {factRevealed && factEntry && (
                        <div
                          className="mt-3 p-3"
                          style={{
                            background: '#ffffff',
                            border: `1px solid ${TF_GOLD}55`,
                            borderRadius: 12,
                          }}
                        >
                          <p
                            className="inline-flex items-center gap-1.5"
                            style={{
                              ...tfEyebrow,
                              marginBottom: 6,
                            }}
                          >
                            <Sparkles
                              className="w-3 h-3"
                              style={{ color: TF_BRAND_GREEN }}
                            />
                            {factEntry.name[language]}
                            {' · '}
                            {t('Fun fact', 'Dato curioso')}
                          </p>
                          <p
                            style={{
                              fontFamily: TF_SERIF,
                              fontStyle: 'italic',
                              fontWeight: 500,
                              fontSize: 17,
                              color: TF_INK,
                              lineHeight: 1.55,
                            }}
                          >
                            {factEntry.fact[language]}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Available Avatars Grid — title attribute surfaces the
                creature name as a native tooltip when the teacher hovers,
                so they learn the avatars as they assign. */}
            <p style={{ ...tfEyebrow, marginBottom: 10 }}>
              {t('Tap an avatar to assign', 'Toca un avatar para asignar')}
            </p>
            <div className="grid grid-cols-4 md:grid-cols-8 gap-2 mb-6">
              {CREATURE_AVATARS.map((emoji, index) => {
                const factEntry = AVATAR_FACTS[emoji];
                return (
                <motion.button
                  key={index}
                  onClick={() => handleAddAvatar(emoji)}
                  whileHover={{ scale: 1.06 }}
                  whileTap={{ scale: 0.94 }}
                  disabled={studentAvatars.length >= 24}
                  title={factEntry?.name[language]}
                  aria-label={factEntry?.name[language] ?? emoji}
                  style={{
                    fontSize: 36,
                    lineHeight: 1,
                    padding: 12,
                    background: TF_CREAM_DEEP,
                    border: `1px solid ${TF_GOLD}33`,
                    borderRadius: 14,
                    cursor: studentAvatars.length >= 24 ? 'not-allowed' : 'pointer',
                    opacity: studentAvatars.length >= 24 ? 0.45 : 1,
                    transition: 'background-color 160ms ease',
                  }}
                >
                  {emoji}
                </motion.button>
                );
              })}
            </div>

            <button
              onClick={() => setPhase('lesson-preview')}
              disabled={studentAvatars.length === 0}
              className="w-full flex items-center justify-center gap-2"
              style={{
                ...tfPrimaryPill,
                padding: '16px 28px',
                fontSize: 20,
                opacity: studentAvatars.length === 0 ? 0.4 : 1,
                cursor: studentAvatars.length === 0 ? 'not-allowed' : 'pointer',
              }}
            >
              {t('Continue', 'Continuar')}
              <ChevronRight className="w-5 h-5" />
            </button>
          </motion.div>
        </div>
      </TeachingPhase>
    );
  }

  // Lesson Preview Phase
  if (phase === 'lesson-preview') {
    return (
      <TeachingPhase onBack={onBack}>
        <div className="mb-6 flex items-center justify-between gap-3 flex-wrap max-w-2xl mx-auto">
          <button onClick={() => setPhase('avatar-assignment')} style={tfOutlinePill}>
            ← {t('Back', 'Volver')}
          </button>
          {sessionChip}
        </div>

        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-6 md:p-8 mb-6"
            style={tfCard}
          >
            <p style={{ ...tfEyebrow, marginBottom: 8 }}>
              {t('Plan', 'Plan')}
            </p>
            <h2 style={{ ...tfH2, marginBottom: 24 }}>
              {integrationProposal
                ? t('Coaching preview', 'Vista previa del coaching')
                : t('Lesson flow', 'Flujo de la lección')}
            </h2>

            {/* ───── Integration preview ─────
                Replaces the editor (and everything below) when a
                structured proposal is loaded. Each row is one proposed
                insert or modify; teacher accepts/skips/edits before
                hitting Apply, which mutates the real lesson plan. */}
            {integrationProposal && (
              <div>
                {/* Top action bar: Apply (primary) + Cancel + count.
                    Lives at the top so the teacher always knows the
                    state and can commit/dismiss without scrolling. */}
                <div
                  className="mb-4 flex items-center justify-between gap-3 flex-wrap"
                >
                  <p
                    style={{
                      fontFamily: TF_SANS,
                      fontSize: 15,
                      fontWeight: 500,
                      color: TF_INK_SOFT,
                      margin: 0,
                    }}
                  >
                    <span style={{ color: TF_BRAND_GREEN, fontWeight: 700 }}>
                      {acceptedChangeIds.size}
                    </span>{' '}
                    {t('of', 'de')} {integrationProposal.changes.length}{' '}
                    {t('changes accepted', 'cambios aceptados')}
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={cancelIntegration}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: TF_INK_SOFT,
                        fontFamily: TF_SANS,
                        fontSize: 15,
                        fontWeight: 600,
                        cursor: 'pointer',
                        padding: '8px 12px',
                      }}
                    >
                      {t('Cancel', 'Cancelar')}
                    </button>
                    <button
                      type="button"
                      onClick={applyAcceptedChanges}
                      disabled={acceptedChangeIds.size === 0}
                      className="inline-flex items-center gap-2"
                      style={{
                        ...tfPrimaryPill,
                        fontSize: 17,
                        padding: '10px 20px',
                        opacity: acceptedChangeIds.size === 0 ? 0.4 : 1,
                        cursor:
                          acceptedChangeIds.size === 0 ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <CheckCircle className="w-4 h-4" strokeWidth={2.5} />
                      {t('Apply to my plan', 'Aplicar a mi plan')}
                      {acceptedChangeIds.size > 0 && ` (${acceptedChangeIds.size})`}
                    </button>
                  </div>
                </div>
                <p
                  style={{
                    fontFamily: TF_SANS,
                    fontWeight: 400,
                    fontSize: 17,
                    color: TF_INK_SOFT,
                    lineHeight: 1.55,
                    marginBottom: 18,
                  }}
                >
                  {t(
                    "Each change weaves coaching into your existing plan. Accept what feels right, edit anything, skip what doesn't — then apply.",
                    'Cada cambio integra el coaching a tu plan. Acepta lo que sientas bien, edita lo que necesites, omite lo demás — luego aplica.',
                  )}
                </p>

                <div className="space-y-3 mb-6">
                  {integrationProposal.changes.map((change, i) => {
                    const accepted = acceptedChangeIds.has(i);
                    const editing = editingChangeIds.has(i);
                    const edits = changeEdits[i] ?? {};

                    // Position label + which existing phase (if any) this references.
                    let positionLabel: string;
                    if (change.kind === 'insert') {
                      if (change.after === -1) {
                        positionLabel = t('Insert before Phase 1', 'Insertar antes de la Fase 1');
                      } else {
                        const refName = lessonPhases[change.after]?.name[language] ?? '';
                        positionLabel = `${t('Insert after Phase', 'Insertar después de la Fase')} ${change.after + 1}${refName ? `: ${refName}` : ''}`;
                      }
                    } else {
                      const refName = lessonPhases[change.target]?.name[language] ?? '';
                      positionLabel = `${t('Refine Phase', 'Refinar la Fase')} ${change.target + 1}${refName ? `: ${refName}` : ''}`;
                    }

                    // Resolve current shown values (edits override, then proposal default).
                    const insertName =
                      change.kind === 'insert'
                        ? (edits.name ?? change.phase.name)
                        : null;
                    const insertDuration =
                      change.kind === 'insert'
                        ? (edits.duration ?? change.phase.duration)
                        : null;
                    const insertDescription =
                      change.kind === 'insert'
                        ? (edits.description ?? change.phase.description)
                        : null;
                    const modifyName =
                      change.kind === 'modify' && change.patch.name
                        ? (edits.name ?? change.patch.name)
                        : null;
                    const modifyDescription =
                      change.kind === 'modify' && change.patch.description
                        ? (edits.description ?? change.patch.description)
                        : null;
                    const modifyDuration =
                      change.kind === 'modify' && change.patch.duration !== undefined
                        ? (edits.duration ?? change.patch.duration)
                        : null;

                    return (
                      <div
                        key={i}
                        style={{
                          background: accepted ? '#ffffff' : TF_CREAM_DEEP,
                          border: accepted
                            ? `1.5px solid ${TF_BRAND_GREEN}55`
                            : `1.5px dashed ${TF_GOLD}88`,
                          borderRadius: 14,
                          padding: 16,
                          opacity: accepted ? 1 : 0.78,
                          transition: 'all 200ms ease',
                        }}
                      >
                        <div className="flex items-baseline justify-between gap-3 mb-2 flex-wrap">
                          <p
                            style={{
                              fontFamily: TF_SANS,
                              fontSize: 13,
                              fontWeight: 700,
                              color: TF_BRAND_GREEN,
                              letterSpacing: '0.08em',
                              textTransform: 'uppercase',
                              margin: 0,
                            }}
                          >
                            {change.kind === 'insert' ? '+ ' : '✎ '}
                            {positionLabel}
                          </p>
                        </div>

                        {change.kind === 'insert' && insertName && (
                          <div className="space-y-1.5 mb-3">
                            <p
                              style={{
                                fontFamily: TF_SANS,
                                fontWeight: 600,
                                fontSize: 19,
                                color: TF_INK,
                                margin: 0,
                              }}
                            >
                              {insertName[language]}
                            </p>
                            <span
                              className="inline-flex items-center gap-1.5"
                              style={{
                                fontFamily: TF_SANS,
                                fontSize: 14,
                                fontWeight: 500,
                                color: TF_MUTED,
                              }}
                            >
                              <Clock className="w-3.5 h-3.5" />
                              {insertDuration} {t('min', 'min')}
                            </span>
                            {insertDescription && (
                              <p
                                style={{
                                  fontFamily: TF_SANS,
                                  fontWeight: 400,
                                  fontSize: 16,
                                  color: TF_INK_SOFT,
                                  lineHeight: 1.55,
                                  marginTop: 6,
                                  marginBottom: 0,
                                }}
                              >
                                {insertDescription[language]}
                              </p>
                            )}
                          </div>
                        )}

                        {change.kind === 'modify' && (
                          <div className="space-y-2.5 mb-3">
                            {modifyName && (
                              <div>
                                <p style={{ ...tfEyebrow, marginBottom: 2 }}>
                                  {t('New name', 'Nuevo nombre')}
                                </p>
                                <p
                                  style={{
                                    fontFamily: TF_SANS,
                                    fontWeight: 600,
                                    fontSize: 18,
                                    color: TF_INK,
                                    margin: 0,
                                  }}
                                >
                                  {modifyName[language]}
                                </p>
                              </div>
                            )}
                            {modifyDescription && (
                              <div>
                                <p style={{ ...tfEyebrow, marginBottom: 2 }}>
                                  {t('New description', 'Nueva descripción')}
                                </p>
                                <p
                                  style={{
                                    fontFamily: TF_SANS,
                                    fontWeight: 400,
                                    fontSize: 16,
                                    color: TF_INK,
                                    lineHeight: 1.55,
                                    margin: 0,
                                  }}
                                >
                                  {modifyDescription[language]}
                                </p>
                              </div>
                            )}
                            {modifyDuration !== null && (
                              <div>
                                <p style={{ ...tfEyebrow, marginBottom: 2 }}>
                                  {t('New duration', 'Nueva duración')}
                                </p>
                                <span
                                  className="inline-flex items-center gap-1.5"
                                  style={{
                                    fontFamily: TF_SANS,
                                    fontWeight: 600,
                                    fontSize: 18,
                                    color: TF_INK,
                                  }}
                                >
                                  <Clock className="w-4 h-4" style={{ color: TF_MUTED }} />
                                  {modifyDuration} {t('min', 'min')}
                                </span>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Inline edit form */}
                        {editing && (
                          <div
                            className="mb-3 p-3 space-y-3"
                            style={{
                              background: TF_CREAM,
                              borderRadius: 10,
                              border: `1px solid ${TF_INK}15`,
                            }}
                          >
                            {((change.kind === 'insert') ||
                              (change.kind === 'modify' && change.patch.name)) && (
                              <div>
                                <p style={{ ...tfEyebrow, marginBottom: 4 }}>
                                  {t('Step name', 'Nombre del paso')}
                                </p>
                                <input
                                  type="text"
                                  value={
                                    (edits.name ??
                                      (change.kind === 'insert'
                                        ? change.phase.name
                                        : change.patch.name!))[language]
                                  }
                                  onChange={(e) => {
                                    const cur =
                                      edits.name ??
                                      (change.kind === 'insert'
                                        ? change.phase.name
                                        : change.patch.name!);
                                    updateChangeEdit(i, {
                                      name: { ...cur, [language]: e.target.value },
                                    });
                                  }}
                                  className="w-full focus:outline-none"
                                  style={{
                                    padding: '8px 12px',
                                    background: '#ffffff',
                                    border: `1px solid ${TF_INK}25`,
                                    borderRadius: 8,
                                    fontFamily: TF_SERIF,
                                    fontStyle: 'italic',
                                    fontWeight: 500,
                                    fontSize: 17,
                                    color: TF_INK,
                                  }}
                                />
                              </div>
                            )}
                            {((change.kind === 'insert') ||
                              (change.kind === 'modify' &&
                                change.patch.duration !== undefined)) && (
                              <div>
                                <p style={{ ...tfEyebrow, marginBottom: 4 }}>
                                  {t('Duration (min)', 'Duración (min)')}
                                </p>
                                <div className="inline-flex items-center gap-2">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const cur =
                                        edits.duration ??
                                        (change.kind === 'insert'
                                          ? change.phase.duration
                                          : change.patch.duration!);
                                      updateChangeEdit(i, {
                                        duration: Math.max(1, cur - 1),
                                      });
                                    }}
                                    style={stepperBtn}
                                  >
                                    −
                                  </button>
                                  <span
                                    style={{
                                      fontFamily: TF_SERIF,
                                      fontWeight: 600,
                                      fontSize: 20,
                                      color: TF_INK,
                                      minWidth: 36,
                                      textAlign: 'center',
                                    }}
                                  >
                                    {edits.duration ??
                                      (change.kind === 'insert'
                                        ? change.phase.duration
                                        : change.patch.duration)}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const cur =
                                        edits.duration ??
                                        (change.kind === 'insert'
                                          ? change.phase.duration
                                          : change.patch.duration!);
                                      updateChangeEdit(i, {
                                        duration: Math.min(99, cur + 1),
                                      });
                                    }}
                                    style={stepperBtn}
                                  >
                                    +
                                  </button>
                                </div>
                              </div>
                            )}
                            {((change.kind === 'insert') ||
                              (change.kind === 'modify' && change.patch.description)) && (
                              <div>
                                <p style={{ ...tfEyebrow, marginBottom: 4 }}>
                                  {t('Description', 'Descripción')}
                                </p>
                                <textarea
                                  value={
                                    (edits.description ??
                                      (change.kind === 'insert'
                                        ? change.phase.description
                                        : change.patch.description!))[language]
                                  }
                                  onChange={(e) => {
                                    const cur =
                                      edits.description ??
                                      (change.kind === 'insert'
                                        ? change.phase.description
                                        : change.patch.description!);
                                    updateChangeEdit(i, {
                                      description: { ...cur, [language]: e.target.value },
                                    });
                                  }}
                                  rows={4}
                                  className="w-full focus:outline-none resize-y"
                                  style={{
                                    padding: '10px 12px',
                                    background: '#ffffff',
                                    border: `1px solid ${TF_INK}25`,
                                    borderRadius: 8,
                                    fontFamily: TF_SERIF,
                                    fontStyle: 'italic',
                                    fontWeight: 500,
                                    fontSize: 16,
                                    color: TF_INK,
                                    lineHeight: 1.55,
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        )}

                        {/* Rationale */}
                        <p
                          className="inline-flex items-baseline gap-1.5"
                          style={{
                            fontFamily: TF_SANS,
                            fontSize: 14,
                            fontWeight: 500,
                            color: TF_INK_SOFT,
                            lineHeight: 1.5,
                            margin: '0 0 12px 0',
                          }}
                        >
                          <Sparkles
                            className="w-3.5 h-3.5 flex-shrink-0"
                            style={{ color: TF_BRAND_GREEN, transform: 'translateY(2px)' }}
                          />
                          <span>{change.rationale[language]}</span>
                        </p>

                        {/* Action toggles: Accept · Edit */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <button
                            type="button"
                            onClick={() => toggleChangeAccepted(i)}
                            className="inline-flex items-center gap-1.5"
                            style={{
                              fontFamily: TF_SANS,
                              fontSize: 14,
                              fontWeight: 600,
                              color: accepted ? '#ffffff' : TF_BRAND_GREEN,
                              background: accepted ? TF_BRAND_GREEN : 'transparent',
                              border: `1.5px solid ${TF_BRAND_GREEN}`,
                              borderRadius: 999,
                              padding: '6px 14px',
                              cursor: 'pointer',
                              transition: 'all 160ms ease',
                            }}
                          >
                            <CheckCircle className="w-3.5 h-3.5" strokeWidth={2.5} />
                            {accepted ? t('Accepted', 'Aceptada') : t('Accept', 'Aceptar')}
                          </button>
                          <button
                            type="button"
                            onClick={() => toggleChangeEditing(i)}
                            className="inline-flex items-center gap-1.5"
                            style={{
                              fontFamily: TF_SANS,
                              fontSize: 14,
                              fontWeight: 600,
                              color: editing ? '#ffffff' : TF_INK,
                              background: editing ? TF_INK : 'transparent',
                              border: `1.5px solid ${TF_INK}40`,
                              borderRadius: 999,
                              padding: '6px 14px',
                              cursor: 'pointer',
                              transition: 'all 160ms ease',
                            }}
                          >
                            <Pencil className="w-3.5 h-3.5" strokeWidth={2.5} />
                            {editing ? t('Done', 'Listo') : t('Edit', 'Editar')}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Pillars referenced (collapsible disclosure) */}
                {integrationProposal.pillars.length > 0 && (
                  <div
                    className="mb-5 p-4"
                    style={{
                      background: TF_CREAM_DEEP,
                      border: `1px solid ${TF_GOLD}33`,
                      borderRadius: 14,
                    }}
                  >
                    <p style={{ ...tfEyebrow, marginBottom: 8 }}>
                      {t(
                        'Wonderpath approach · principles referenced',
                        'Enfoque Wonderpath · principios de referencia',
                      )}
                    </p>
                    <ul className="space-y-2">
                      {integrationProposal.pillars.map((pillar) => (
                        <li key={pillar.id}>
                          <p
                            style={{
                              fontFamily: TF_SANS,
                              fontWeight: 600,
                              fontSize: 15,
                              color: TF_INK,
                              margin: 0,
                            }}
                          >
                            {pillar.title[language]}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

              </div>
            )}

            {!integrationProposal && (<>

            {/* Editable timeline — reorder, rename, change duration, remove */}
            <div className="mb-5 relative">
              {lessonPhases.length > 1 && (
                <div
                  style={{
                    position: 'absolute',
                    left: 14,
                    top: 0,
                    bottom: 0,
                    width: 2,
                    background: `${TF_GOLD}66`,
                  }}
                />
              )}
              <div className="space-y-3">
                {lessonPhases.map((lessonPhase, index) => {
                  const editing = editingPhaseId === lessonPhase.id;
                  const canMoveUp = index > 0;
                  const canMoveDown = index < lessonPhases.length - 1;

                  return (
                    <div
                      key={lessonPhase.id}
                      className="relative flex items-start gap-4"
                    >
                      <div
                        className="flex-shrink-0 flex items-center justify-center relative z-10"
                        style={{
                          width: 30,
                          height: 30,
                          background: '#ffffff',
                          border: `1.5px solid ${TF_GOLD}`,
                          color: TF_INK,
                          borderRadius: '50%',
                          fontFamily: TF_SERIF,
                          fontWeight: 600,
                          fontSize: 17,
                        }}
                      >
                        {index + 1}
                      </div>
                      <div
                        className="flex-1"
                        style={{
                          background: editing ? '#ffffff' : TF_CREAM_DEEP,
                          borderRadius: 12,
                          border: editing
                            ? `1.5px solid ${TF_BRAND_GREEN}`
                            : `1px solid ${TF_GOLD}33`,
                          padding: editing ? 14 : 12,
                          transition: 'background 160ms ease, border-color 160ms ease',
                        }}
                      >
                        {editing ? (
                          // ── Inline editor ─────────────────────────────
                          <div className="space-y-3">
                            <div>
                              <p style={{ ...tfEyebrow, marginBottom: 6 }}>
                                {t('Step name', 'Nombre del paso')}
                              </p>
                              <input
                                type="text"
                                value={lessonPhase.name[language]}
                                onChange={(e) =>
                                  updateLessonPhase(lessonPhase.id, {
                                    name: {
                                      ...lessonPhase.name,
                                      [language]: e.target.value,
                                    },
                                  })
                                }
                                autoFocus
                                className="w-full focus:outline-none"
                                style={{
                                  padding: '8px 12px',
                                  background: TF_CREAM,
                                  border: `1px solid ${TF_INK}25`,
                                  borderRadius: 10,
                                  fontFamily: TF_SERIF,
                                  fontStyle: 'italic',
                                  fontWeight: 500,
                                  fontSize: 18,
                                  color: TF_INK,
                                }}
                                onFocus={(e) => { e.currentTarget.style.borderColor = TF_BRAND_GREEN; }}
                                onBlur={(e) => { e.currentTarget.style.borderColor = `${TF_INK}25`; }}
                              />
                            </div>
                            <div>
                              <p style={{ ...tfEyebrow, marginBottom: 6 }}>
                                {t('Duration (min)', 'Duración (min)')}
                              </p>
                              <div className="inline-flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() =>
                                    updateLessonPhase(lessonPhase.id, {
                                      duration: Math.max(1, lessonPhase.duration - 1),
                                    })
                                  }
                                  aria-label={t('Decrease duration', 'Disminuir duración')}
                                  style={stepperBtn}
                                >
                                  −
                                </button>
                                <span
                                  style={{
                                    fontFamily: TF_SERIF,
                                    fontWeight: 600,
                                    fontSize: 20,
                                    color: TF_INK,
                                    minWidth: 36,
                                    textAlign: 'center',
                                  }}
                                >
                                  {lessonPhase.duration}
                                </span>
                                <button
                                  type="button"
                                  onClick={() =>
                                    updateLessonPhase(lessonPhase.id, {
                                      duration: Math.min(99, lessonPhase.duration + 1),
                                    })
                                  }
                                  aria-label={t('Increase duration', 'Aumentar duración')}
                                  style={stepperBtn}
                                >
                                  +
                                </button>
                              </div>
                            </div>
                            <div>
                              <p style={{ ...tfEyebrow, marginBottom: 6 }}>
                                {t('Description & facilitation', 'Descripción y facilitación')}
                              </p>
                              <textarea
                                value={lessonPhase.description?.[language] ?? ''}
                                onChange={(e) =>
                                  updateLessonPhase(lessonPhase.id, {
                                    description: {
                                      en: language === 'en'
                                        ? e.target.value
                                        : lessonPhase.description?.en ?? '',
                                      es: language === 'es'
                                        ? e.target.value
                                        : lessonPhase.description?.es ?? '',
                                    },
                                  })
                                }
                                rows={5}
                                placeholder={t(
                                  'How does this step run? What should the teacher say or notice? Edit the suggestion or write your own.',
                                  '¿Cómo se desarrolla este paso? ¿Qué debe decir o notar la maestra/o? Edita la sugerencia o escribe la tuya.',
                                )}
                                className="w-full focus:outline-none resize-y"
                                style={{
                                  padding: '12px 14px',
                                  background: TF_CREAM,
                                  border: `1px solid ${TF_INK}25`,
                                  borderRadius: 12,
                                  fontFamily: TF_SERIF,
                                  fontStyle: 'italic',
                                  fontWeight: 500,
                                  fontSize: 17,
                                  color: TF_INK,
                                  lineHeight: 1.55,
                                  minHeight: 100,
                                  transition: 'border-color 160ms ease',
                                }}
                                onFocus={(e) => { e.currentTarget.style.borderColor = TF_BRAND_GREEN; }}
                                onBlur={(e) => { e.currentTarget.style.borderColor = `${TF_INK}25`; }}
                              />
                              <p
                                className="mt-2 inline-flex items-center gap-1.5"
                                style={{
                                  fontFamily: TF_SANS,
                                  fontSize: 14,
                                  fontWeight: 500,
                                  color: TF_MUTED,
                                }}
                              >
                                <Sparkles className="w-3 h-3" style={{ color: TF_BRAND_GREEN }} />
                                {t(
                                  'Seeded from the Wonderpath teaching pack — fully editable.',
                                  'Sugerido desde el cuaderno docente Wonderpath — totalmente editable.',
                                )}
                              </p>
                            </div>
                            <div className="flex justify-end">
                              <button
                                type="button"
                                onClick={() => setEditingPhaseId(null)}
                                style={{
                                  ...tfPrimaryPill,
                                  padding: '8px 18px',
                                  fontSize: 16,
                                  boxShadow: 'none',
                                }}
                              >
                                {t('Done', 'Listo')}
                              </button>
                            </div>
                          </div>
                        ) : (
                          // ── Compact row with reorder + edit + remove + description toggle ──
                          (() => {
                            const desc = lessonPhase.description?.[language]?.trim() ?? '';
                            const expanded = expandedDescriptionIds.has(lessonPhase.id);
                            const toggleDesc = () => {
                              setExpandedDescriptionIds((prev) => {
                                const next = new Set(prev);
                                if (next.has(lessonPhase.id)) next.delete(lessonPhase.id);
                                else next.add(lessonPhase.id);
                                return next;
                              });
                            };
                            return (
                              <>
                                <div className="flex items-start justify-between gap-3">
                                  <div className="min-w-0 flex-1">
                                    <p
                                      style={{
                                        fontFamily: TF_SERIF,
                                        fontStyle: 'italic',
                                        fontWeight: 500,
                                        fontSize: 18,
                                        color: TF_INK,
                                      }}
                                    >
                                      {lessonPhase.name[language]}
                                    </p>
                                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                                      <span className="inline-flex items-center gap-1.5">
                                        <Clock
                                          className="w-3.5 h-3.5"
                                          style={{ color: TF_MUTED }}
                                        />
                                        <span
                                          style={{
                                            fontFamily: TF_SANS,
                                            fontSize: 14,
                                            fontWeight: 500,
                                            color: TF_MUTED,
                                          }}
                                        >
                                          {lessonPhase.duration} {t('min', 'min')}
                                        </span>
                                      </span>
                                      {desc && (
                                        <button
                                          type="button"
                                          onClick={toggleDesc}
                                          aria-expanded={expanded}
                                          className="inline-flex items-center gap-1"
                                          style={{
                                            background: 'transparent',
                                            border: 'none',
                                            padding: 0,
                                            fontFamily: TF_SANS,
                                            fontSize: 14,
                                            fontWeight: 600,
                                            color: TF_BRAND_GREEN,
                                            cursor: 'pointer',
                                          }}
                                        >
                                          {expanded
                                            ? t('Hide details', 'Ocultar detalles')
                                            : t('Show details', 'Ver detalles')}
                                          {expanded ? (
                                            <ChevronUp className="w-3 h-3" />
                                          ) : (
                                            <ChevronDown className="w-3 h-3" />
                                          )}
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1 flex-shrink-0">
                                    <button
                                      type="button"
                                      onClick={() => movePhase(index, -1)}
                                      disabled={!canMoveUp}
                                      aria-label={t('Move up', 'Subir')}
                                      style={iconBtn(canMoveUp)}
                                    >
                                      <ArrowUp className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => movePhase(index, 1)}
                                      disabled={!canMoveDown}
                                      aria-label={t('Move down', 'Bajar')}
                                      style={iconBtn(canMoveDown)}
                                    >
                                      <ArrowDown className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setEditingPhaseId(lessonPhase.id)}
                                      aria-label={t('Edit step', 'Editar paso')}
                                      style={iconBtn(true)}
                                    >
                                      <Pencil className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => removeLessonPhase(lessonPhase.id)}
                                      aria-label={t('Remove step', 'Quitar paso')}
                                      style={iconBtn(true, TF_SOFT_RED)}
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                </div>
                                {expanded && desc && (
                                  <p
                                    className="mt-3"
                                    style={{
                                      fontFamily: TF_SERIF,
                                      fontStyle: 'italic',
                                      fontWeight: 500,
                                      fontSize: 17,
                                      color: TF_INK_SOFT,
                                      lineHeight: 1.6,
                                      paddingTop: 12,
                                      borderTop: `1px solid ${TF_GOLD}33`,
                                      whiteSpace: 'pre-wrap',
                                    }}
                                  >
                                    {desc}
                                  </p>
                                )}
                              </>
                            );
                          })()
                        )}
                      </div>
                    </div>
                  );
                })}
                {lessonPhases.length === 0 && (
                  <p
                    className="text-center py-6"
                    style={{
                      fontFamily: TF_SERIF,
                      fontStyle: 'italic',
                      fontSize: 18,
                      color: TF_MUTED,
                    }}
                  >
                    {t(
                      'Your flow is empty. Add a suggested step below to start.',
                      'Tu flujo está vacío. Agrega un paso sugerido abajo para empezar.',
                    )}
                  </p>
                )}
              </div>
            </div>

            {/* AI suggestions tray — fetches personalized phase ideas
                from /api/lesson-plan-coach (Stage 2). Falls back to the
                static SUGGESTED_PHASES list if the API is unreachable. */}
            <div
              className="mb-6"
              style={{
                background: '#ffffff',
                border: `1px solid ${TF_GOLD}55`,
                borderRadius: 18,
                overflow: 'hidden',
              }}
            >
              <div className="flex items-start justify-between gap-2 p-4">
                <button
                  type="button"
                  onClick={() => {
                    const next = !showSuggestions;
                    setShowSuggestions(next);
                    // Lazy-load on first open: only fetch if we don't have
                    // suggestions yet and aren't already loading.
                    if (next && aiSuggestions === null && !suggestionsLoading) {
                      void fetchAiSuggestions();
                    }
                  }}
                  className="flex-1 min-w-0 text-left"
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
                >
                  <p
                    className="inline-flex items-center gap-1.5"
                    style={{ ...tfEyebrow, marginBottom: 4 }}
                  >
                    <Sparkles className="w-3.5 h-3.5" style={{ color: TF_BRAND_GREEN }} />
                    {t('WonderGuidance suggests', 'WonderGuidance sugiere')}
                  </p>
                  <p
                    style={{
                      fontFamily: TF_SERIF,
                      fontStyle: 'italic',
                      fontWeight: 500,
                      fontSize: 18,
                      color: TF_INK,
                    }}
                  >
                    {t(
                      'Add a step from the Wonderpath approach.',
                      'Agrega un paso desde el enfoque Wonderpath.',
                    )}
                  </p>
                </button>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {showSuggestions && (
                    <button
                      type="button"
                      onClick={() => void fetchAiSuggestions()}
                      disabled={suggestionsLoading}
                      aria-label={t('Refresh suggestions', 'Actualizar sugerencias')}
                      title={t('Refresh suggestions', 'Actualizar sugerencias')}
                      style={iconBtn(!suggestionsLoading)}
                    >
                      <Sparkles
                        className="w-3.5 h-3.5"
                        style={{
                          animation: suggestionsLoading
                            ? 'wp-spin 1.4s linear infinite'
                            : undefined,
                        }}
                      />
                    </button>
                  )}
                  <span style={{ ...iconBtn(true), pointerEvents: 'none' }}>
                    {showSuggestions ? (
                      <ChevronUp className="w-3.5 h-3.5" />
                    ) : (
                      <Plus className="w-3.5 h-3.5" />
                    )}
                  </span>
                </div>
              </div>

              {showSuggestions && (() => {
                const suggestions: SuggestedPhase[] =
                  aiSuggestions && aiSuggestions.length > 0 ? aiSuggestions : SUGGESTED_PHASES;
                const isPersonalized = aiSuggestions !== null && aiSuggestions.length > 0;
                return (
                  <div className="px-4 pb-4 space-y-2">
                    {suggestionsLoading && (
                      <CoachingProgress stages={COACHING_SUGGEST_STAGES} />
                    )}
                    {!suggestionsLoading && suggestionsError && (
                      <p
                        className="px-1"
                        style={{
                          fontFamily: TF_SANS,
                          fontSize: 14,
                          fontWeight: 500,
                          color: TF_MUTED,
                        }}
                      >
                        {suggestionsError}
                      </p>
                    )}
                    {!suggestionsLoading && suggestions.map((suggestion, i) => {
                      const alreadyAdded = lessonPhases.some(
                        (p) => p.name.en.trim().toLowerCase() === suggestion.name.en.trim().toLowerCase(),
                      );
                      return (
                        <div
                          key={i}
                          className="flex items-start gap-3 p-3"
                          style={{
                            background: TF_CREAM_DEEP,
                            borderRadius: 12,
                            border: `1px solid ${TF_GOLD}33`,
                            opacity: alreadyAdded ? 0.5 : 1,
                          }}
                        >
                          <div className="min-w-0 flex-1">
                            <p
                              style={{
                                fontFamily: TF_SERIF,
                                fontStyle: 'italic',
                                fontWeight: 500,
                                fontSize: 17,
                                color: TF_INK,
                              }}
                            >
                              {suggestion.name[language]}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <Clock className="w-3 h-3" style={{ color: TF_MUTED }} />
                              <p
                                style={{
                                  fontFamily: TF_SANS,
                                  fontSize: 14,
                                  fontWeight: 500,
                                  color: TF_MUTED,
                                }}
                              >
                                {suggestion.duration} {t('min', 'min')}
                              </p>
                            </div>
                            {suggestion.description?.[language] && (
                              <p
                                className="mt-2"
                                style={{
                                  fontFamily: TF_SERIF,
                                  fontStyle: 'italic',
                                  fontWeight: 500,
                                  fontSize: 16,
                                  color: TF_INK_SOFT,
                                  lineHeight: 1.5,
                                }}
                              >
                                {suggestion.description[language]}
                              </p>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => addSuggestedPhase(suggestion)}
                            disabled={alreadyAdded}
                            className="flex-shrink-0 inline-flex items-center gap-1"
                            style={{
                              padding: '6px 14px',
                              background: alreadyAdded ? 'transparent' : TF_BRAND_GREEN,
                              color: alreadyAdded ? TF_MUTED : '#ffffff',
                              border: alreadyAdded
                                ? `1.5px solid ${TF_MUTED}55`
                                : 'none',
                              borderRadius: 999,
                              fontFamily: TF_SANS,
                              fontSize: 14,
                              fontWeight: 600,
                              cursor: alreadyAdded ? 'default' : 'pointer',
                            }}
                          >
                            {alreadyAdded ? (
                              t('Added', 'Agregado')
                            ) : (
                              <>
                                <Plus className="w-3 h-3" />
                                {t('Add', 'Agregar')}
                              </>
                            )}
                          </button>
                        </div>
                      );
                    })}
                    {!suggestionsLoading && (
                      <p
                        style={{
                          fontFamily: TF_SANS,
                          fontSize: 14,
                          fontWeight: 500,
                          color: TF_MUTED,
                          textAlign: 'center',
                          marginTop: 8,
                        }}
                      >
                        {isPersonalized
                          ? t(
                              'Personalized for your focus area and current plan.',
                              'Personalizado para tu área de enfoque y tu plan actual.',
                            )
                          : t(
                              'Curated from the Wonderpath approach — tap the sparkle to refresh for personalized ideas.',
                              'Curado desde el enfoque Wonderpath — toca la chispa para actualizar con ideas personalizadas.',
                            )}
                      </p>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Hands-on play ideas tray — curated, no-screen alternatives.
                Activities are organized into categories (nature, pretend,
                forts, drawing, story, movement, sound). Each card adds to
                the lesson plan as a phase, with materials + vocabulary
                folded into the description so the teacher has the full
                script in one place during class. */}
            <div
              className="mb-6"
              style={{
                background: '#ffffff',
                border: `1px solid ${TF_GOLD}55`,
                borderRadius: 18,
                overflow: 'hidden',
              }}
            >
              <button
                type="button"
                onClick={() => setShowPlayIdeas((v) => !v)}
                className="w-full flex items-start justify-between gap-3 p-4 text-left"
                style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
              >
                <div className="min-w-0 flex-1">
                  <p
                    className="inline-flex items-center gap-1.5"
                    style={{ ...tfEyebrow, marginBottom: 4 }}
                  >
                    <Sparkles className="w-3.5 h-3.5" style={{ color: TF_GOLD }} />
                    {t('Hands-on play ideas · no screens', 'Ideas de juego sin pantallas')}
                  </p>
                  <p
                    style={{
                      fontFamily: TF_SERIF,
                      fontStyle: 'italic',
                      fontWeight: 500,
                      fontSize: 18,
                      color: TF_INK,
                    }}
                  >
                    {t(
                      'Nature, pretend play, forts, drawing — for kids exploring and learning words.',
                      'Naturaleza, juego de imaginación, fuertes, dibujo — para niños/as que exploran y aprenden palabras.',
                    )}
                  </p>
                </div>
                <span style={{ ...iconBtn(true), pointerEvents: 'none' }}>
                  {showPlayIdeas ? (
                    <ChevronUp className="w-3.5 h-3.5" />
                  ) : (
                    <Plus className="w-3.5 h-3.5" />
                  )}
                </span>
              </button>

              {showPlayIdeas && (
                <div className="px-4 pb-4">
                  {/* Category filter chips — All + each category */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    <button
                      type="button"
                      onClick={() => setPlayFilter(null)}
                      style={{
                        padding: '6px 14px',
                        borderRadius: 999,
                        fontFamily: TF_SANS,
                        fontSize: 14,
                        fontWeight: 600,
                        border: `1.5px solid ${TF_BRAND_GREEN}`,
                        background: playFilter === null ? TF_BRAND_GREEN : 'transparent',
                        color: playFilter === null ? '#ffffff' : TF_BRAND_GREEN,
                        cursor: 'pointer',
                      }}
                    >
                      {t('All', 'Todas')}
                    </button>
                    {(Object.keys(PLAY_CATEGORY_LABELS) as PlayCategory[]).map((cat) => (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => setPlayFilter(cat)}
                        style={{
                          padding: '6px 14px',
                          borderRadius: 999,
                          fontFamily: TF_SANS,
                          fontSize: 14,
                          fontWeight: 600,
                          border: `1.5px solid ${TF_BRAND_GREEN}`,
                          background: playFilter === cat ? TF_BRAND_GREEN : 'transparent',
                          color: playFilter === cat ? '#ffffff' : TF_BRAND_GREEN,
                          cursor: 'pointer',
                        }}
                      >
                        {PLAY_CATEGORY_LABELS[cat][language]}
                      </button>
                    ))}
                  </div>

                  <div className="space-y-2">
                    {PLAY_ACTIVITIES.filter(
                      (a) => playFilter === null || a.category === playFilter,
                    ).map((activity) => {
                      const expanded = expandedActivityIds.has(activity.id);
                      const alreadyAdded = lessonPhases.some(
                        (p) => p.name.en.trim().toLowerCase() === activity.name.en.trim().toLowerCase(),
                      );
                      const toggleExpand = () => {
                        setExpandedActivityIds((prev) => {
                          const next = new Set(prev);
                          if (next.has(activity.id)) next.delete(activity.id);
                          else next.add(activity.id);
                          return next;
                        });
                      };
                      return (
                        <div
                          key={activity.id}
                          style={{
                            background: TF_CREAM_DEEP,
                            borderRadius: 12,
                            border: `1px solid ${TF_GOLD}33`,
                            opacity: alreadyAdded ? 0.55 : 1,
                            padding: 12,
                          }}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <p
                                className="inline-flex items-center gap-2"
                                style={{ marginBottom: 4 }}
                              >
                                <span
                                  style={{
                                    fontFamily: TF_SANS,
                                    fontSize: 14,
                                    fontWeight: 600,
                                    letterSpacing: '0.16em',
                                    textTransform: 'uppercase',
                                    color: TF_GOLD,
                                    background: '#ffffff',
                                    padding: '2px 8px',
                                    borderRadius: 999,
                                    border: `1px solid ${TF_GOLD}55`,
                                  }}
                                >
                                  {PLAY_CATEGORY_LABELS[activity.category][language]}
                                </span>
                              </p>
                              <p
                                style={{
                                  fontFamily: TF_SERIF,
                                  fontStyle: 'italic',
                                  fontWeight: 500,
                                  fontSize: 18,
                                  color: TF_INK,
                                }}
                              >
                                {activity.name[language]}
                              </p>
                              <div className="flex items-center gap-3 mt-1 flex-wrap">
                                <span className="inline-flex items-center gap-1.5">
                                  <Clock className="w-3 h-3" style={{ color: TF_MUTED }} />
                                  <span
                                    style={{
                                      fontFamily: TF_SANS,
                                      fontSize: 14,
                                      fontWeight: 500,
                                      color: TF_MUTED,
                                    }}
                                  >
                                    {activity.duration} {t('min', 'min')}
                                  </span>
                                </span>
                                <button
                                  type="button"
                                  onClick={toggleExpand}
                                  aria-expanded={expanded}
                                  className="inline-flex items-center gap-1"
                                  style={{
                                    background: 'transparent',
                                    border: 'none',
                                    padding: 0,
                                    fontFamily: TF_SANS,
                                    fontSize: 14,
                                    fontWeight: 600,
                                    color: TF_BRAND_GREEN,
                                    cursor: 'pointer',
                                  }}
                                >
                                  {expanded
                                    ? t('Hide details', 'Ocultar detalles')
                                    : t('Show details', 'Ver detalles')}
                                  {expanded ? (
                                    <ChevronUp className="w-3 h-3" />
                                  ) : (
                                    <ChevronDown className="w-3 h-3" />
                                  )}
                                </button>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => addPlayActivity(activity)}
                              disabled={alreadyAdded}
                              className="flex-shrink-0 inline-flex items-center gap-1"
                              style={{
                                padding: '6px 14px',
                                background: alreadyAdded ? 'transparent' : TF_BRAND_GREEN,
                                color: alreadyAdded ? TF_MUTED : '#ffffff',
                                border: alreadyAdded
                                  ? `1.5px solid ${TF_MUTED}55`
                                  : 'none',
                                borderRadius: 999,
                                fontFamily: TF_SANS,
                                fontSize: 14,
                                fontWeight: 600,
                                cursor: alreadyAdded ? 'default' : 'pointer',
                              }}
                            >
                              {alreadyAdded ? (
                                t('Added', 'Agregado')
                              ) : (
                                <>
                                  <Plus className="w-3 h-3" />
                                  {t('Add', 'Agregar')}
                                </>
                              )}
                            </button>
                          </div>

                          {expanded && (
                            <div
                              className="mt-3 pt-3 space-y-3"
                              style={{ borderTop: `1px solid ${TF_GOLD}33` }}
                            >
                              <p
                                style={{
                                  fontFamily: TF_SERIF,
                                  fontStyle: 'italic',
                                  fontWeight: 500,
                                  fontSize: 17,
                                  color: TF_INK_SOFT,
                                  lineHeight: 1.6,
                                }}
                              >
                                {activity.description[language]}
                              </p>
                              {activity.materials[language].length > 0 && (
                                <div>
                                  <p style={{ ...tfEyebrow, marginBottom: 4 }}>
                                    {t('Materials', 'Materiales')}
                                  </p>
                                  <p style={{ ...tfBody, fontSize: 16 }}>
                                    {activity.materials[language].join(' · ')}
                                  </p>
                                </div>
                              )}
                              {activity.vocabulary[language].length > 0 && (
                                <div>
                                  <p style={{ ...tfEyebrow, marginBottom: 4 }}>
                                    {t('Vocabulary to seed', 'Vocabulario para sembrar')}
                                  </p>
                                  <div className="flex flex-wrap gap-1.5">
                                    {activity.vocabulary[language].map((word) => (
                                      <span
                                        key={word}
                                        style={{
                                          fontFamily: TF_SERIF,
                                          fontStyle: 'italic',
                                          fontSize: 16,
                                          fontWeight: 500,
                                          color: TF_INK,
                                          background: '#ffffff',
                                          padding: '3px 10px',
                                          borderRadius: 999,
                                          border: `1px solid ${TF_GOLD}55`,
                                        }}
                                      >
                                        {word}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Dynamic total */}
            <div
              className="p-4 mb-6"
              style={{
                background: TF_CREAM_DEEP,
                borderLeft: `3px solid ${TF_GOLD}`,
                borderRadius: 12,
              }}
            >
              <p
                style={{
                  fontFamily: TF_SERIF,
                  fontStyle: 'italic',
                  fontSize: 18,
                  fontWeight: 500,
                  color: TF_INK,
                  marginBottom: 6,
                }}
              >
                {t('Total lesson time:', 'Tiempo total de la lección:')}{' '}
                ~{lessonPhases.reduce((sum, p) => sum + p.duration, 0)}{' '}
                {t('minutes', 'minutos')}
              </p>
              <p style={tfBody}>
                {t(
                  'Times are flexible — adjust based on your students\' engagement.',
                  'Los tiempos son flexibles: ajusta según el compromiso de tus estudiantes.'
                )}
              </p>
            </div>

            {/* Stage 3 — Review my plan inline panel.
                Renders above the action buttons. The CTA below toggles
                the panel open + triggers the fetch. Stays in-card,
                never a modal. */}
            {(reviewLoading || reviewError || reviewPanel) && (
              <div
                className="mb-5 p-5 md:p-6"
                style={{
                  background: '#ffffff',
                  border: `1px solid ${TF_GOLD}55`,
                  borderRadius: 18,
                }}
              >
                <div className="flex items-start justify-between gap-3 mb-4">
                  <p
                    className="inline-flex items-center gap-1.5 min-w-0 flex-1"
                    style={tfEyebrow}
                  >
                    <Sparkles className="w-3.5 h-3.5" style={{ color: TF_BRAND_GREEN }} />
                    {t(
                      'Wonderpath coaching recommendations',
                      'Recomendaciones del coaching Wonderpath',
                    )}
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setReviewPanel(null);
                      setReviewError(null);
                    }}
                    aria-label={t('Close', 'Cerrar')}
                    style={iconBtn(true)}
                  >
                    ×
                  </button>
                </div>

                {reviewLoading && (
                  <CoachingProgress stages={COACHING_REVIEW_STAGES} />
                )}

                {!reviewLoading && reviewError && (
                  <p
                    style={{
                      fontFamily: TF_SERIF,
                      fontStyle: 'italic',
                      fontWeight: 500,
                      fontSize: 17,
                      color: TF_SOFT_RED,
                    }}
                  >
                    {reviewError}
                  </p>
                )}

                {!reviewLoading && reviewPanel && (
                  <div className="space-y-5">
                    {reviewPanel.strengths.length > 0 && (
                      <div>
                        <p style={{ ...tfEyebrow, marginBottom: 8 }}>
                          {t('Where your plan is strong', 'Dónde tu plan está fuerte')}
                        </p>
                        <ul className="space-y-2">
                          {reviewPanel.strengths.map((bullet, i) => (
                            <li
                              key={i}
                              className="flex gap-2"
                              style={{
                                fontFamily: TF_SANS,
                                fontWeight: 400,
                                fontSize: 17,
                                color: TF_INK,
                                lineHeight: 1.55,
                              }}
                            >
                              <span style={{ color: TF_BRAND_GREEN, flexShrink: 0 }}>·</span>
                              <span>{bullet}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {reviewPanel.considerations.length > 0 && (
                      <div>
                        <p style={{ ...tfEyebrow, marginBottom: 8 }}>
                          {t('Coaching invitations', 'Invitaciones del coaching')}
                        </p>
                        <ul className="space-y-3">
                          {reviewPanel.considerations.map((bullet, i) => (
                            <li
                              key={i}
                              className="flex gap-2 items-start"
                              style={{
                                fontFamily: TF_SANS,
                                fontWeight: 400,
                                fontSize: 17,
                                color: TF_INK,
                                lineHeight: 1.55,
                              }}
                            >
                              <span style={{ color: TF_GOLD, flexShrink: 0 }}>·</span>
                              <span>{bullet}</span>
                            </li>
                          ))}
                        </ul>

                        {/* Single primary action: send the invitations
                            back to Claude as structured changes the
                            teacher will preview, edit, and apply on the
                            real plan — no more append-to-bottom. */}
                        <div className="mt-4">
                          {integrationLoading ? (
                            <CoachingProgress stages={COACHING_INTEGRATE_STAGES} />
                          ) : integrationEmpty ? (
                            <div
                              className="flex items-start justify-between gap-3 p-3"
                              style={{
                                background: TF_CREAM_DEEP,
                                border: `1px solid ${TF_BRAND_GREEN}33`,
                                borderRadius: 12,
                              }}
                            >
                              <p
                                className="inline-flex items-baseline gap-2 flex-1"
                                style={{
                                  fontFamily: TF_SERIF,
                                  fontStyle: 'italic',
                                  fontWeight: 500,
                                  fontSize: 16,
                                  color: TF_INK,
                                  margin: 0,
                                  lineHeight: 1.5,
                                }}
                              >
                                <Sparkles
                                  className="w-4 h-4 flex-shrink-0"
                                  style={{ color: TF_BRAND_GREEN, transform: 'translateY(2px)' }}
                                />
                                <span>
                                  {t(
                                    'No structural changes needed — your plan reads clean. The invitations above are gentle nudges to fold in as you teach.',
                                    'Sin cambios estructurales — tu plan se lee claro. Las invitaciones de arriba son matices suaves para integrar al enseñar.',
                                  )}
                                </span>
                              </p>
                              <button
                                type="button"
                                onClick={() => setIntegrationEmpty(false)}
                                aria-label={t('Dismiss', 'Cerrar')}
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  color: TF_INK_SOFT,
                                  fontSize: 18,
                                  cursor: 'pointer',
                                  padding: '0 4px',
                                  flexShrink: 0,
                                }}
                              >
                                ×
                              </button>
                            </div>
                          ) : integrationError ? (
                            <div
                              className="flex items-start justify-between gap-3 p-3"
                              style={{
                                background: '#ffffff',
                                border: `1px solid ${TF_SOFT_RED}55`,
                                borderRadius: 12,
                              }}
                            >
                              <p
                                style={{
                                  fontFamily: TF_SERIF,
                                  fontStyle: 'italic',
                                  fontWeight: 500,
                                  fontSize: 16,
                                  color: TF_SOFT_RED,
                                  margin: 0,
                                  flex: 1,
                                }}
                              >
                                {integrationError}
                              </p>
                              <button
                                type="button"
                                onClick={() => setIntegrationError(null)}
                                aria-label={t('Dismiss', 'Cerrar')}
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  color: TF_INK_SOFT,
                                  fontSize: 18,
                                  cursor: 'pointer',
                                  padding: '0 4px',
                                  flexShrink: 0,
                                }}
                              >
                                ×
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => void fetchIntegrationProposal()}
                              disabled={lessonPhases.length === 0}
                              className="inline-flex items-center gap-2"
                              style={{
                                ...tfPrimaryPill,
                                fontSize: 17,
                                padding: '12px 22px',
                                opacity: lessonPhases.length === 0 ? 0.4 : 1,
                                cursor:
                                  lessonPhases.length === 0 ? 'not-allowed' : 'pointer',
                              }}
                            >
                              <Sparkles className="w-4 h-4" />
                              {t(
                                'Integrate coaching into my plan →',
                                'Integrar coaching a mi plan →',
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {reviewPanel.pillars.length > 0 && (
                      <div
                        style={{
                          background: TF_CREAM_DEEP,
                          border: `1px solid ${TF_GOLD}33`,
                          borderRadius: 14,
                          padding: 14,
                        }}
                      >
                        <p style={{ ...tfEyebrow, marginBottom: 10 }}>
                          {t(
                            'Wonderpath approach · principles referenced',
                            'Enfoque Wonderpath · principios de referencia',
                          )}
                        </p>
                        <ul className="space-y-3">
                          {reviewPanel.pillars.map((pillar) => (
                            <li key={pillar.id}>
                              <p
                                style={{
                                  fontFamily: TF_SERIF,
                                  fontStyle: 'italic',
                                  fontWeight: 600,
                                  fontSize: 17,
                                  color: TF_INK,
                                  marginBottom: 2,
                                }}
                              >
                                {pillar.title[language]}
                              </p>
                              <p style={{ ...tfBody, fontSize: 16 }}>
                                {pillar.description[language]}
                              </p>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {reviewPanel.strengths.length === 0 &&
                      reviewPanel.considerations.length === 0 && (
                        <p
                          style={{
                            fontFamily: TF_SERIF,
                            fontStyle: 'italic',
                            fontSize: 17,
                            color: TF_MUTED,
                          }}
                        >
                          {t(
                            'No coaching notes this time — your plan reads clean.',
                            'Sin notas del coaching esta vez — tu plan se lee claro.',
                          )}
                        </p>
                      )}
                  </div>
                )}
              </div>
            )}

            {/* Action buttons — Review (outline) + Run the class (primary) */}
            <div className="flex flex-col-reverse sm:flex-row gap-3">
              <button
                onClick={() => void fetchPlanReview()}
                disabled={reviewLoading || lessonPhases.length === 0}
                className="sm:flex-shrink-0 flex items-center justify-center gap-2"
                style={{
                  ...tfOutlinePill,
                  padding: '14px 24px',
                  fontSize: 18,
                  opacity: reviewLoading || lessonPhases.length === 0 ? 0.55 : 1,
                  cursor:
                    reviewLoading || lessonPhases.length === 0 ? 'not-allowed' : 'pointer',
                }}
              >
                <Sparkles
                  className="w-4 h-4"
                  style={{
                    animation: reviewLoading ? 'wp-spin 1.4s linear infinite' : undefined,
                  }}
                />
                {reviewLoading
                  ? t('Coaching…', 'Coaching…')
                  : t('Get coaching recommendations', 'Obtener recomendaciones')}
              </button>
              <button
                onClick={() => {
                  setPhase('in-session');
                  startLessonPhase(0);
                }}
                disabled={lessonPhases.length === 0}
                className="flex-1 flex items-center justify-center gap-2"
                style={{
                  ...tfPrimaryPill,
                  padding: '16px 28px',
                  fontSize: 20,
                  opacity: lessonPhases.length === 0 ? 0.4 : 1,
                  cursor: lessonPhases.length === 0 ? 'not-allowed' : 'pointer',
                }}
              >
                {t('Run the class', 'Iniciar la clase')}
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            </>)}
          </motion.div>
        </div>

        {/* Spin keyframe used by the Sparkles refresh + review buttons. */}
        <style>{`
          @keyframes wp-spin {
            from { transform: rotate(0deg); }
            to   { transform: rotate(360deg); }
          }
        `}</style>
      </TeachingPhase>
    );
  }

  // In-Session Phase
  if (phase === 'in-session') {
    const currentPhase = lessonPhases[currentLessonPhase];
    if (!currentPhase) {
      // Edge case: every phase was removed in the editor. Bounce back to
      // the lesson preview so the teacher can rebuild the flow.
      setPhase('lesson-preview');
      return null;
    }
    const prompts = currentPhase.prompts[language];

    // Special handling for the device-picker step. We key off phase id
    // (3 = "Show selected game" in the default flow) so the picker still
    // surfaces correctly even after the teacher reorders or removes
    // earlier steps.
    if (currentPhase.id === 3 && hasDevice === null) {
      return (
        <TeachingPhase onBack={onBack}>
          <div className="max-w-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-6 md:p-8"
              style={tfCard}
            >
              <p style={{ ...tfEyebrow, marginBottom: 8 }}>
                {currentPhase.name[language]}
              </p>
              <h2 style={{ ...tfH2, marginBottom: 8 }}>
                {t('Device or no device?', '¿Con dispositivo o sin él?')}
              </h2>
              <p style={{ ...tfBodyItalic, marginBottom: 24 }}>
                {t(
                  'Will you use a device for sharing the game in the classroom?',
                  '¿Usarás un dispositivo para compartir el juego en el aula?'
                )}
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => setHasDevice(true)}
                  className="w-full"
                  style={{ ...tfPrimaryPill, padding: '16px 28px', fontSize: 18 }}
                >
                  {t('Yes, with device', 'Sí, con dispositivo')}
                </button>
                <button
                  onClick={() => setHasDevice(false)}
                  className="w-full"
                  style={{ ...tfOutlinePill, padding: '14px 28px', fontSize: 18 }}
                >
                  {t('No, without device', 'No, sin dispositivo')}
                </button>
              </div>
            </motion.div>
          </div>
        </TeachingPhase>
      );
    }

    return (
      <TeachingPhase onBack={onBack}>
        <div className="max-w-2xl mx-auto">
          {sessionChip}
          {/* Progress Timeline - Compact + clickable.
              Every phase pill is a button so the teacher can hop to any
              phase at any time (e.g. back to capture mid-review, then
              forward again to keep tagging photos). The current phase
              acts as a no-op. */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-4 mb-4"
            style={tfCard}
          >
            <div className="flex items-center gap-2 overflow-x-auto pb-1">
              {lessonPhases.map((lessonPhase, index) => {
                const isCompleted = index < currentLessonPhase;
                const isCurrent = index === currentLessonPhase;
                return (
                  <div key={lessonPhase.id} className="flex items-center flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => {
                        if (isCurrent) return;
                        setCurrentLessonPhase(index);
                        setCurrentPromptIndex(0);
                      }}
                      aria-current={isCurrent ? 'step' : undefined}
                      aria-label={`${t('Phase', 'Fase')} ${lessonPhase.id}: ${lessonPhase.name[language]}`}
                      className="flex flex-col items-center"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        padding: 0,
                        cursor: isCurrent ? 'default' : 'pointer',
                      }}
                    >
                      <div
                        className="flex items-center justify-center"
                        style={{
                          width: 30,
                          height: 30,
                          borderRadius: '50%',
                          fontFamily: TF_SERIF,
                          fontWeight: 600,
                          fontSize: 16,
                          background: isCompleted
                            ? TF_BRAND_GREEN
                            : isCurrent
                              ? '#ffffff'
                              : TF_CREAM_DEEP,
                          color: isCompleted
                            ? '#ffffff'
                            : isCurrent
                              ? TF_INK
                              : TF_MUTED,
                          border: isCurrent ? `1.5px solid ${TF_BRAND_GREEN}` : `1px solid ${TF_GOLD}33`,
                          boxShadow: isCurrent ? `0 0 0 3px ${TF_BRAND_GREEN}22` : 'none',
                          transition: 'all 200ms ease',
                        }}
                      >
                        {isCompleted ? '✓' : lessonPhase.id}
                      </div>
                      <p
                        className="text-center max-w-[64px] mt-1"
                        style={{
                          fontFamily: TF_SANS,
                          fontSize: 14,
                          fontWeight: isCurrent ? 600 : 500,
                          color: isCurrent ? TF_INK : TF_MUTED,
                        }}
                      >
                        {lessonPhase.name[language].split(' ')[0]}
                      </p>
                    </button>
                    {index < lessonPhases.length - 1 && (
                      <div
                        style={{
                          width: 24,
                          height: 2,
                          background: isCompleted ? TF_BRAND_GREEN : `${TF_GOLD}55`,
                          marginBottom: 18,
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </motion.div>

          {/* Phase Header — title + the editable description so the
              teacher always sees the "why" alongside the cue prompts.
              Description was set in the Lesson flow editor (seeded from
              the teaching pack, then the teacher's own edits). */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-6 mb-4"
            style={tfCard}
          >
            <p style={{ ...tfEyebrow, marginBottom: 6 }}>
              {t('Phase', 'Fase')} {currentLessonPhase + 1} / {lessonPhases.length}
            </p>
            <h2 style={tfH2}>
              {currentPhase.name[language]}
            </h2>
            {currentPhase.description?.[language]?.trim() && (
              <p
                style={{
                  fontFamily: TF_SERIF,
                  fontStyle: 'italic',
                  fontWeight: 500,
                  fontSize: 'clamp(17px, 1.9vw, 20px)',
                  color: TF_INK_SOFT,
                  lineHeight: 1.6,
                  marginTop: 12,
                  paddingTop: 12,
                  borderTop: `1px solid ${TF_GOLD}33`,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {currentPhase.description[language]}
              </p>
            )}
          </motion.div>

          {/* Phase id 2 = "Learners get Avatars" — live, in-class reveal.
              Surfaces the assigned avatars with a tap-to-reveal fact, so
              the teacher can read each fact aloud as they hand the card
              to the child. */}
          {currentPhase.id === 2 && studentAvatars.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-5 md:p-6 mb-4"
              style={tfCard}
            >
              <p style={{ ...tfEyebrow, marginBottom: 6 }}>
                {t('Reveal moment', 'Momento de revelación')}
              </p>
              <p
                style={{
                  fontFamily: TF_SERIF,
                  fontStyle: 'italic',
                  fontWeight: 500,
                  fontSize: 18,
                  color: TF_INK,
                  marginBottom: 16,
                }}
              >
                {t(
                  'Tap each avatar as you hand the card to the child — read the fact aloud as their welcome.',
                  'Toca cada avatar al entregar la tarjeta al niño/a — lee el dato en voz alta como bienvenida.',
                )}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {studentAvatars.map((avatar, index) => {
                  const factEntry = AVATAR_FACTS[avatar.emoji];
                  const factRevealed = revealedFactIndex === index;
                  return (
                    <button
                      key={index}
                      type="button"
                      onClick={() =>
                        setRevealedFactIndex(factRevealed ? null : index)
                      }
                      aria-pressed={factRevealed}
                      className="text-left"
                      style={{
                        background: factRevealed ? '#ffffff' : TF_CREAM_DEEP,
                        border: factRevealed
                          ? `1.5px solid ${TF_BRAND_GREEN}`
                          : `1px solid ${TF_GOLD}33`,
                        borderRadius: 14,
                        padding: 14,
                        cursor: 'pointer',
                        transition: 'background 160ms ease, border-color 160ms ease',
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <span style={{ fontSize: 40, lineHeight: 1 }}>{avatar.emoji}</span>
                        <div className="min-w-0 flex-1">
                          <p
                            style={{
                              fontFamily: TF_SERIF,
                              fontStyle: 'italic',
                              fontWeight: 600,
                              fontSize: 19,
                              color: TF_INK,
                              lineHeight: 1.2,
                            }}
                          >
                            {avatar.name?.trim() || factEntry?.name[language] || '—'}
                          </p>
                          {factEntry && avatar.name?.trim() && (
                            <p
                              style={{
                                fontFamily: TF_SANS,
                                fontSize: 14,
                                fontWeight: 500,
                                color: TF_MUTED,
                                marginTop: 2,
                              }}
                            >
                              {factEntry.name[language]}
                            </p>
                          )}
                        </div>
                        {factEntry && (
                          <span
                            aria-hidden
                            style={{
                              ...iconBtn(true),
                              flexShrink: 0,
                              color: factRevealed ? TF_BRAND_GREEN : TF_INK,
                              background: factRevealed ? `${TF_BRAND_GREEN}15` : 'transparent',
                              borderColor: factRevealed
                                ? TF_BRAND_GREEN
                                : `${TF_INK}25`,
                            }}
                          >
                            <Sparkles className="w-3.5 h-3.5" />
                          </span>
                        )}
                      </div>
                      {factRevealed && factEntry && (
                        <p
                          className="mt-3"
                          style={{
                            fontFamily: TF_SERIF,
                            fontStyle: 'italic',
                            fontWeight: 500,
                            fontSize: 17,
                            color: TF_INK,
                            lineHeight: 1.55,
                            paddingTop: 12,
                            borderTop: `1px solid ${TF_GOLD}33`,
                          }}
                        >
                          {factEntry.fact[language]}
                        </p>
                      )}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Phase id 6 = "Document creations" — lean capture + improved gallery */}
          {currentPhase.id === 6 ? (
            <PhotoCaptureGallery
              language={language as 'en' | 'es'}
              photos={session.photos}
              avatars={studentAvatars.map((a, i) => ({
                index: i,
                emoji: a.emoji,
                name: a.name || undefined,
              }))}
              onCapture={session.addPhotoFromVideo}
              onToggleStudentTag={togglePhotoStudentTag}
              onDelete={session.removePhoto}
              onJumpToReview={() => {
                // Jump to whichever phase carries id 7 (the review surface)
                const reviewIdx = lessonPhases.findIndex((p) => p.id === 7);
                if (reviewIdx >= 0) {
                  setCurrentLessonPhase(reviewIdx);
                  setCurrentPromptIndex(0);
                }
              }}
            />
          ) : currentPhase.id === 3 && hasDevice !== null ? (
            /* Phase 3: Show Simplified Game */
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-6 md:p-8 mb-4"
              style={tfCard}
            >
              <div className="mb-5">
                <h3 style={{ ...tfH2, fontSize: 'clamp(24px, 3vw, 32px)', marginBottom: 6 }}>
                  {selectedFocus?.gameTitle[language]}
                </h3>
                <p style={tfBody}>
                  {hasDevice
                    ? t(
                        'Use this simplified version to guide students. They will create on paper.',
                        'Usa esta versión simplificada para guiar a los estudiantes. Crearán en papel.'
                      )
                    : t(
                        'Follow these prompts to guide the activity without a device.',
                        'Sigue estas indicaciones para guiar la actividad sin dispositivo.'
                      )}
                </p>
              </div>

              {/* Render appropriate simplified game based on selected focus */}
              {hasDevice && selectedFocus?.game === 'animal-abcs' && (
                <AnimalABCsSimplified language={language} />
              )}
              {hasDevice && selectedFocus?.game === 'pepe-breathing' && (
                <PepeBreathingSimplified language={language} />
              )}
              {hasDevice && selectedFocus?.game === 'peke-story' && (
                <PekeStorySimplified language={language} />
              )}
              {hasDevice && selectedFocus?.game === 'superpowers' && (
                <SuperpowersCardsSimplified language={language} />
              )}

              {/* If no device, show the regular prompts */}
              {!hasDevice && (
                <div className="space-y-3">
                  {prompts.map((prompt, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-3 p-3"
                      style={{
                        background: TF_CREAM_DEEP,
                        borderRadius: 14,
                        border: `1px solid ${TF_GOLD}33`,
                      }}
                    >
                      <div
                        className="flex-shrink-0 flex items-center justify-center"
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          background: '#ffffff',
                          color: TF_INK,
                          border: `1.5px solid ${TF_GOLD}`,
                          fontFamily: TF_SERIF,
                          fontWeight: 600,
                          fontSize: 16,
                        }}
                      >
                        {index + 1}
                      </div>
                      <p
                        className="flex-1"
                        style={{
                          fontFamily: TF_SERIF,
                          fontStyle: 'italic',
                          fontSize: 18,
                          fontWeight: 500,
                          color: TF_INK,
                          lineHeight: 1.45,
                        }}
                      >
                        {prompt}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          ) : (
            /* Regular Prompt Card for other phases. */
            <motion.div
              initial={{ opacity: 0, x: 16 }}
              animate={{ opacity: 1, x: 0 }}
              className="p-6 md:p-8 mb-4"
              style={tfCard}
            >
              <div className="flex items-start gap-3 mb-5">
                <div
                  className="flex-shrink-0 flex items-center justify-center"
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    background: '#ffffff',
                    color: TF_INK,
                    border: `1.5px solid ${TF_GOLD}`,
                    fontFamily: TF_SERIF,
                    fontWeight: 600,
                    fontSize: 17,
                  }}
                >
                  {currentPromptIndex + 1}
                </div>
                <p
                  className="flex-1"
                  style={{
                    fontFamily: TF_SERIF,
                    fontStyle: 'italic',
                    fontSize: 'clamp(21px, 2.8vw, 26px)',
                    fontWeight: 500,
                    color: TF_INK,
                    lineHeight: 1.45,
                  }}
                >
                  {prompts[currentPromptIndex]}
                </p>
              </div>

              {currentPromptIndex < prompts.length - 1 && (
                <button
                  onClick={nextPrompt}
                  className="w-full flex items-center justify-center gap-2"
                  style={{
                    ...tfOutlinePill,
                    width: '100%',
                    padding: '12px 20px',
                    fontSize: 18,
                  }}
                >
                  {t('Next cue', 'Siguiente indicación')}
                  <ChevronRight className="w-5 h-5" />
                </button>
              )}
            </motion.div>
          )}

          {/* Phase id 7 = "Sharing cycle" — full review & edit surface for photos */}
          {currentPhase.id === 7 && (
            <PhotoReviewList
              language={language as 'en' | 'es'}
              photos={session.photos}
              avatars={studentAvatars.map((a, i) => ({
                index: i,
                emoji: a.emoji,
                name: a.name || undefined,
              }))}
              onUpdatePhoto={(photoId, patch) => session.patchPhoto(photoId, patch)}
              onDelete={session.removePhoto}
              onJumpToCapture={() => {
                const captureIdx = lessonPhases.findIndex((p) => p.id === 6);
                if (captureIdx >= 0) {
                  setCurrentLessonPhase(captureIdx);
                  setCurrentPromptIndex(0);
                }
              }}
            />
          )}

          {/* Phase Navigation — Previous (outline) + Next/Complete (primary).
              Previous is hidden on phase 0 since there's nothing to go back
              to within the in-session flow. */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-5"
            style={tfCard}
          >
            <div className="flex flex-col-reverse sm:flex-row gap-3">
              {currentLessonPhase > 0 && (
                <button
                  onClick={() => {
                    setCurrentLessonPhase(currentLessonPhase - 1);
                    setCurrentPromptIndex(0);
                  }}
                  className="sm:flex-shrink-0 flex items-center justify-center gap-2"
                  style={{
                    ...tfOutlinePill,
                    padding: '14px 24px',
                    fontSize: 18,
                  }}
                >
                  <ChevronLeft className="w-5 h-5" />
                  {t('Previous phase', 'Fase anterior')}
                </button>
              )}
              <button
                onClick={advanceToNextPhase}
                className="flex-1 flex items-center justify-center gap-2"
                style={{
                  ...tfPrimaryPill,
                  padding: '16px 28px',
                  fontSize: 20,
                }}
              >
                {currentLessonPhase < lessonPhases.length - 1
                  ? t('Next phase', 'Siguiente fase')
                  : t('Complete lesson', 'Completar lección')}
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </motion.div>
        </div>
      </TeachingPhase>
    );
  }

  // Reflection Phase
  if (phase === 'reflection') {
    return (
      <TeachingPhase onBack={onBack}>
        <div className="max-w-2xl mx-auto">
          {sessionChip}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-6 md:p-8 mb-6"
            style={tfCard}
          >
            <p style={{ ...tfEyebrow, marginBottom: 8 }}>
              {t('Reflect', 'Reflexiona')}
            </p>
            <h2 style={{ ...tfH2, marginBottom: 28 }}>
              {t('Teacher reflection', 'Reflexión del maestro')}
            </h2>

            <div className="space-y-5 mb-7">
              <ReflectionField
                label={t('What surprised you today?', '¿Qué te sorprendió hoy?')}
                value={reflectionNotes.surprises ?? ''}
                onChange={(v) =>
                  setReflectionNotes((prev) => ({ ...prev, surprises: v }))
                }
              />
              <ReflectionField
                label={t('Which students showed growth?', '¿Qué estudiantes mostraron crecimiento?')}
                value={reflectionNotes.growth ?? ''}
                onChange={(v) =>
                  setReflectionNotes((prev) => ({ ...prev, growth: v }))
                }
              />
              <ReflectionField
                label={t('What would you adjust for next time?', '¿Qué ajustarías para la próxima vez?')}
                value={reflectionNotes.adjustments ?? ''}
                onChange={(v) =>
                  setReflectionNotes((prev) => ({ ...prev, adjustments: v }))
                }
              />
            </div>

            <button
              onClick={() => setPhase('complete')}
              className="w-full flex items-center justify-center gap-2"
              style={{
                ...tfPrimaryPill,
                padding: '16px 28px',
                fontSize: 20,
              }}
            >
              {t('Complete & create artifact', 'Completar y crear artefacto')}
              <CheckCircle className="w-5 h-5" />
            </button>
          </motion.div>
        </div>
      </TeachingPhase>
    );
  }

  // Complete Phase
  if (phase === 'complete') {
    const summaryRows: { label: string; value: React.ReactNode }[] = [
      { label: t('Focus area', 'Área de enfoque'), value: selectedFocus?.name[language] ?? '—' },
      { label: t('Game', 'Juego'), value: selectedFocus?.gameTitle[language] ?? '—' },
      ...(studentAvatars.length > 0
        ? [{ label: t('Students', 'Estudiantes'), value: String(studentAvatars.length) }]
        : []),
      { label: t('Photos captured', 'Fotos capturadas'), value: String(session.photos.length) },
      { label: t('Duration', 'Duración'), value: `~60 ${t('minutes', 'minutos')}` },
      ...(session.code
        ? [{
            label: t('Session code', 'Código de sesión'),
            value: <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{session.code}</span>,
          }]
        : []),
    ];

    return (
      <TeachingPhase onBack={onBack}>
        <div className="max-w-2xl mx-auto">
          {sessionChip}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-6 md:p-8 text-center"
            style={tfCard}
          >
            {/* Soft cream success badge with brand-green check */}
            <div
              className="flex items-center justify-center mx-auto mb-6"
              style={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                background: TF_CREAM_DEEP,
                border: `1.5px solid ${TF_GOLD}66`,
              }}
            >
              <CheckCircle style={{ width: 36, height: 36, color: TF_BRAND_GREEN }} />
            </div>

            <p style={{ ...tfEyebrow, marginBottom: 8 }}>
              {t('Final step · lesson complete', 'Paso final · lección completa')}
            </p>
            <h2 style={{ ...tfH2, marginBottom: 12 }}>
              {t('Create your artifact', 'Crea tu artefacto')}
            </h2>

            <p style={{ ...tfBodyItalic, fontSize: 'clamp(19px, 2.4vw, 23px)', marginBottom: 28, maxWidth: 480, marginLeft: 'auto', marginRight: 'auto' }}>
              {t(
                "Pick a format and choose what to include — including your reflection notes if you'd like to share them with families.",
                'Elige un formato y qué incluir — incluyendo tus notas de reflexión si quieres compartirlas con las familias.',
              )}
            </p>

            {/* Lesson Summary */}
            <div
              className="p-5 mb-6 text-left"
              style={{
                background: TF_CREAM_DEEP,
                borderRadius: 18,
                border: `1px solid ${TF_GOLD}33`,
              }}
            >
              <p style={{ ...tfEyebrow, marginBottom: 12 }}>
                {t('Lesson summary', 'Resumen de la lección')}
              </p>
              <dl className="space-y-1.5">
                {summaryRows.map((row) => (
                  <div key={row.label} className="flex items-baseline gap-3">
                    <dt
                      style={{
                        fontFamily: TF_SANS,
                        fontSize: 16,
                        fontWeight: 600,
                        color: TF_MUTED,
                        minWidth: 130,
                      }}
                    >
                      {row.label}
                    </dt>
                    <dd
                      style={{
                        fontFamily: TF_SERIF,
                        fontStyle: 'italic',
                        fontWeight: 500,
                        fontSize: 18,
                        color: TF_INK,
                      }}
                    >
                      {row.value}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>

            {/* Artifact Picker */}
            <div
              className="p-6 mb-6 text-left"
              style={{
                background: '#ffffff',
                border: `1px solid ${TF_CARD_BORDER}`,
                borderRadius: 18,
              }}
            >
              <p style={{ ...tfEyebrow, marginBottom: 4 }}>
                {t('Artifact', 'Artefacto')}
              </p>
              <p style={{ ...tfBody, marginBottom: 18 }}>
                {t(
                  "Choose a format and what to include. We'll build the file for you to download.",
                  'Elige un formato y qué incluir. Crearemos el archivo para que lo descargues.',
                )}
              </p>

              {/* Format choice */}
              <p style={{ ...tfEyebrow, marginBottom: 8 }}>
                {t('Format', 'Formato')}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
                {[
                  { id: 'pdf' as const, label: t('PDF report', 'Informe PDF'), hint: t('Photos · captions · optional notes', 'Fotos · descripciones · notas opcionales') },
                  { id: 'images' as const, label: t('Photos with captions', 'Fotos con descripciones'), hint: t('JPGs · caption baked into each image', 'JPG · descripción incluida en cada imagen') },
                ].map((opt) => {
                  const selected = artifactFormat === opt.id;
                  const disabled = opt.id === 'images' && session.photos.length === 0;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => !disabled && setArtifactFormat(opt.id)}
                      disabled={disabled}
                      className="text-left p-3"
                      style={{
                        borderRadius: 14,
                        border: selected ? `2px solid ${TF_BRAND_GREEN}` : `1px solid ${TF_CARD_BORDER}`,
                        background: selected ? `${TF_BRAND_GREEN}10` : '#ffffff',
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        opacity: disabled ? 0.45 : 1,
                        transition: 'border-color 160ms ease, background-color 160ms ease',
                      }}
                    >
                      <p
                        style={{
                          fontFamily: TF_SERIF,
                          fontStyle: 'italic',
                          fontWeight: 600,
                          fontSize: 18,
                          color: TF_INK,
                          marginBottom: 2,
                        }}
                      >
                        {opt.label}
                      </p>
                      <p style={{ fontFamily: TF_SANS, fontSize: 14, color: TF_MUTED }}>
                        {opt.hint}
                      </p>
                    </button>
                  );
                })}
              </div>

              {/* Include checkboxes */}
              {artifactFormat !== 'images' && (
                <>
                  <p style={{ ...tfEyebrow, marginBottom: 8 }}>
                    {t('Include', 'Incluir')}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {[
                      { key: 'photos' as const, label: t('Photos', 'Fotos'), count: session.photos.length },
                      { key: 'reflection' as const, label: t('Reflection notes', 'Notas de reflexión'), count: Object.values(reflectionNotes).filter(Boolean).length },
                      { key: 'avatars' as const, label: t('Student avatars', 'Avatares de estudiantes'), count: studentAvatars.length },
                    ].map((opt) => {
                      const checked = artifactInclude[opt.key];
                      return (
                        <label
                          key={opt.key}
                          className="inline-flex items-center gap-2 cursor-pointer"
                          style={{
                            padding: '8px 14px',
                            borderRadius: 999,
                            border: `1.5px solid ${checked ? TF_BRAND_GREEN : `${TF_INK}25`}`,
                            background: checked ? `${TF_BRAND_GREEN}10` : 'transparent',
                            color: checked ? TF_INK : TF_INK_SOFT,
                            fontFamily: TF_SANS,
                            fontSize: 17,
                            fontWeight: 600,
                            transition: 'border-color 160ms ease, background-color 160ms ease',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) =>
                              setArtifactInclude((prev) => ({
                                ...prev,
                                [opt.key]: e.target.checked,
                              }))
                            }
                            style={{ accentColor: TF_BRAND_GREEN }}
                          />
                          <span>{opt.label}</span>
                          {opt.count > 0 && (
                            <span
                              style={{
                                fontSize: 14,
                                background: TF_CREAM_DEEP,
                                color: TF_INK_SOFT,
                                padding: '2px 8px',
                                borderRadius: 999,
                              }}
                            >
                              {opt.count}
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                </>
              )}

              <button
                onClick={() => {
                  const reportArgs = {
                    code: session.code ?? '',
                    language: language as 'en' | 'es',
                    focusArea: selectedFocus?.name[language] ?? '',
                    gameTitle: selectedFocus?.gameTitle[language] ?? '',
                    avatars: studentAvatars,
                    reflection: reflectionNotes,
                    photos: session.photos,
                  };
                  if (artifactFormat === 'pdf') downloadPdfReport(reportArgs, artifactInclude);
                  else if (artifactFormat === 'images') {
                    void downloadAllPhotosWithCaptions(
                      session.photos,
                      studentAvatars,
                      language as 'en' | 'es',
                    );
                  }
                }}
                className="w-full inline-flex items-center justify-center gap-2"
                style={{
                  ...tfPrimaryPill,
                  padding: '14px 24px',
                  fontSize: 18,
                }}
              >
                <Download className="w-5 h-5" />
                {artifactFormat === 'images'
                  ? t('Download photos with captions', 'Descargar fotos con descripciones')
                  : t('Save as PDF', 'Guardar como PDF')}
              </button>
              {artifactFormat === 'pdf' && (
                <p
                  className="text-center mt-3"
                  style={{
                    fontFamily: TF_SANS,
                    fontSize: 14,
                    color: TF_MUTED,
                  }}
                >
                  {t(
                    'A print dialog will open — choose "Save as PDF" as the destination.',
                    'Se abrirá el diálogo de impresión — elige "Guardar como PDF" como destino.',
                  )}
                </p>
              )}
            </div>

            {/* Photo Gallery preview */}
            {session.photos.length > 0 && (
              <div
                className="p-6 mb-6 text-left"
                style={{
                  background: '#ffffff',
                  border: `1px solid ${TF_CARD_BORDER}`,
                  borderRadius: 18,
                }}
              >
                <p style={{ ...tfEyebrow, marginBottom: 16 }}>
                  {t('Photo gallery', 'Galería de fotos')}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {session.photos.map((photo) => (
                    <div
                      key={photo.id}
                      style={{
                        background: TF_CREAM,
                        border: `1px solid ${TF_CARD_BORDER}`,
                        borderRadius: 14,
                        overflow: 'hidden',
                      }}
                    >
                      <img
                        src={photo.publicUrl}
                        alt={photo.caption || 'Student work'}
                        className="w-full h-32 object-cover"
                        loading="lazy"
                      />
                      <div className="p-3 space-y-2">
                        {photo.caption && (
                          <p
                            style={{
                              fontFamily: TF_SERIF,
                              fontStyle: 'italic',
                              fontSize: 16,
                              color: TF_INK,
                            }}
                          >
                            {photo.caption}
                          </p>
                        )}
                        <div className="flex flex-wrap gap-1.5 items-center">
                          {photo.student_tags.map((idx) => (
                            <span key={idx} style={{ fontSize: 20, lineHeight: 1 }} aria-hidden>
                              {studentAvatars[idx]?.emoji ?? '•'}
                            </span>
                          ))}
                          {photo.skill_tags.map((s) => {
                            const color = SKILL_TAG_COLORS[s];
                            return (
                              <span
                                key={s}
                                style={{
                                  fontSize: 14,
                                  fontWeight: 600,
                                  fontFamily: TF_SANS,
                                  letterSpacing: '0.06em',
                                  textTransform: 'uppercase',
                                  padding: '2px 8px',
                                  borderRadius: 999,
                                  background: color.bg,
                                  color: color.fg,
                                }}
                              >
                                {SKILL_TAG_LABELS[s][language]}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-col gap-3">
              <button
                onClick={onBack}
                className="w-full"
                style={{ ...tfPrimaryPill, padding: '16px 28px', fontSize: 18 }}
              >
                {t('Return to home', 'Volver al inicio')}
              </button>
              <button
                onClick={() => {
                  // Re-run with same code: keep session code, avatars,
                  // focus, lesson plan, and any captured photos. Just
                  // rewind in-session progress + the avatar-fact reveal,
                  // and bounce back to the lesson preview so the teacher
                  // can adjust the plan based on what they learned.
                  setCurrentLessonPhase(0);
                  setRevealedFactIndex(null);
                  setPhase('lesson-preview');
                }}
                className="w-full flex items-center justify-center gap-2"
                style={{ ...tfOutlinePill, width: '100%', padding: '14px 28px' }}
              >
                <RotateCcw className="w-4 h-4" strokeWidth={2.25} />
                {t('Re-run with same code', 'Reiniciar con el mismo código')}
              </button>
              <button
                onClick={() => {
                  session.clear();
                  setPhase('welcome');
                  setSelectedFocus(null);
                  setStudentAvatars([]);
                  setCurrentLessonPhase(0);
                  setReflectionNotes({});
                  hydratedRef.current = false;
                }}
                className="w-full"
                style={{ ...tfOutlinePill, width: '100%', padding: '14px 28px' }}
              >
                {t('Start new lesson', 'Comenzar nueva lección')}
              </button>
            </div>
          </motion.div>
        </div>
      </TeachingPhase>
    );
  }

  return null;
}
